import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Search,
  X,
  FileText,
  User,
  Clock,
  Users,
  Eye,
  Download,
  FileSpreadsheet,
  FileText as FileTextIcon,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import { getData } from "../../../Services/ProfileServices";
import { fetchGrievanceData, getThemeURL, getAuditHistory, getAllAuditHistory } from "../../../Services/EndPoints";
import { postDataWithBody } from "../../../Services/Services";
import Loader from "react-spinner-loader";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";

const AuditHistory = () => {
  const navigate = useNavigate();
  const [loader, setLoader] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [complaints, setComplaints] = useState([]);
  const [filteredComplaints, setFilteredComplaints] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [userId, setUserId] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("All Status");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const [exporting, setExporting] = useState(false);
  const [isExportDropdownOpen, setIsExportDropdownOpen] = useState(false);
  const [expandedTickets, setExpandedTickets] = useState(new Set()); // Added for expandable functionality
  const [dateError, setDateError] = useState("");
  const [dateInputKey, setDateInputKey] = useState(0);
  const statusOptions = [
    "All Status",
    "Open",
    "In Progress",
    "Issue Pending at User Level",
    "Issue Pending at External Vendor",
    "Resolved",
    "Closed",
    "Reopen",
  ];

  // Calculate pagination
  const totalItems = filteredComplaints.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
  const currentItems = filteredComplaints.slice(startIndex, endIndex);

  // Toggle ticket expansion
  const toggleTicketExpansion = (ticketId) => {
    setExpandedTickets(prev => {
      const newSet = new Set(prev);
      if (newSet.has(ticketId)) {
        newSet.delete(ticketId);
      } else {
        newSet.add(ticketId);
      }
      return newSet;
    });
  };
// UPDATED: Correct extraction of Resolution Date & Time from API response
// 
// The resolution date/time can appear in multiple places:
// 1. In TICKET_CREATION event's details.resolutionDateTime
// 2. In STATUS_CHANGE event's details.resolutionDateTime
// 
// We need to find the LATEST resolutionDateTime across all events
const processAuditHistoryData = (apiResponse) => {
  const events = apiResponse.auditHistory.map((auditEvent, index) => {
    let event = null;
    
    // Get the next event to check for reassignment
    const nextEvent = apiResponse.auditHistory[index + 1];
    const isFollowedByReassignment = nextEvent?.eventType === "REASSIGNMENT";
    
    switch (auditEvent.eventType) {
      case "TICKET_CREATION":
        let creationAction = `Ticket was created`;
        
        // Check if there's an assigned user in the creation event itself
        if (auditEvent.details?.assignedTo) {
          creationAction += ` → Assigned to ${auditEvent.details.assignedTo}`;
        }
        // Check if followed by REASSIGNMENT event
        else if (isFollowedByReassignment && nextEvent.details) {
          const reassignTo = nextEvent.details.reassignmentUser || 
                           nextEvent.details.newL1User || 
                           nextEvent.details.newL2User || "User";
          creationAction += ` → Assigned to ${reassignTo}`;
        }
        
        event = {
          type: "Ticket Creation",
          timestamp: auditEvent.timestamp,
          user: auditEvent.performedBy || "System",
          userId: auditEvent.performedById,
          performedByUserLevel: auditEvent.performedByUserLevel || auditEvent.details?.escalationLevel || "N/A",
          details: creationAction,
          description: auditEvent.details?.description || apiResponse.subject || "",
          priority: auditEvent.details?.priority || "N/A",
          category: auditEvent.details?.category,
          subcategory: auditEvent.details?.subcategory,
          subject: auditEvent.details?.subject,
          preferredResolutionMethod: auditEvent.details?.preferredResolutionMethod,
          branchName: auditEvent.details?.branchName,
          requestorEmail: auditEvent.details?.requestorEmail,
          bankName: auditEvent.details?.bankName,
          supportTeam: auditEvent.details?.supportTeam,
          reportedBy: auditEvent.details?.reportedBy,
          escalationLevel: auditEvent.details?.escalationLevel,
          assignedTo: auditEvent.details?.assignedTo,
          ticketCreatedDateTime: auditEvent.details?.ticketCreatedDateTime,
          requestorContactNumber: auditEvent.details?.requestorContactNumber,
          requestorUserId: auditEvent.details?.requestorUserId,
          originalSaveLevel: auditEvent.originalSaveLevel,
          eventType: auditEvent.eventType,
          resolutionDateTime: auditEvent.details?.resolutionDateTime,
          icon: "📋",
          color: "#3B82F6",
          slaPaused: false,
          hasReassignment: isFollowedByReassignment && !auditEvent.details?.assignedTo,
          reassignmentDetails: (isFollowedByReassignment && !auditEvent.details?.assignedTo) ? nextEvent.details : null
        };
        break;
        
      case "STATUS_CHANGE":
        const oldStatus = auditEvent.details?.oldStatus || "Open";
        const newStatus = auditEvent.details?.newStatus || "Open";
        
        let statusDetails = `Status changed from ${oldStatus} to ${newStatus}`;
        
        // Check if followed by REASSIGNMENT event
        if (isFollowedByReassignment && nextEvent.details) {
          const reassignTo = nextEvent.details.reassignmentUser || 
                           nextEvent.details.newL1User || 
                           nextEvent.details.newL2User || "User";
          statusDetails += ` → Reassigned to ${reassignTo}`;
        }
        
        event = {
          type: "Status Change",
          timestamp: auditEvent.timestamp,
          user: auditEvent.performedBy || "System",
          userId: auditEvent.performedById,
          performedByUserLevel: auditEvent.performedByUserLevel || auditEvent.details?.performedByUserLevel || "N/A",
          details: statusDetails,
          previous: oldStatus,
          current: newStatus,
          rootCause: auditEvent.details?.rootCause,
          oldStatusId: auditEvent.details?.oldStatusId,
          newStatusId: auditEvent.details?.newStatusId,
          oldStatusMapId: auditEvent.details?.oldStatusMapId,
          statusChangeDateTime: auditEvent.details?.statusChangeDateTime,
          actionTakenBy: auditEvent.details?.actionTakenBy,
          actionDateTime: auditEvent.details?.actionDateTime,
          actionMessage: auditEvent.details?.actionMessage,
          resolutionSteps: auditEvent.details?.resolutionSteps,
          resolutionDateTime: auditEvent.details?.resolutionDateTime,
          originalSaveLevel: auditEvent.originalSaveLevel,
          eventType: auditEvent.eventType,
          icon: "🔄",
          color: "#8B5CF6",
          slaPaused: auditEvent.details?.slaPaused || false,
          slaPausedAt: auditEvent.details?.slaPausedAt || null,
          hasReassignment: isFollowedByReassignment,
          reassignmentDetails: isFollowedByReassignment ? nextEvent.details : null
        };
        break;
        
      case "COMMENT":
        event = {
          type: "Comment Added",
          timestamp: auditEvent.timestamp,
          user: auditEvent.performedBy || "System",
          userId: auditEvent.performedById,
          performedByUserLevel: auditEvent.performedByUserLevel || "N/A",
          details: auditEvent.details?.comment || "",
          commentDateTime: auditEvent.details?.commentDateTime,
          commentedBy: auditEvent.details?.commentedBy,
          originalSaveLevel: auditEvent.originalSaveLevel,
          eventType: auditEvent.eventType,
          icon: "💬",
          color: "#F59E0B",
          slaPaused: false
        };
        break;
        
      case "ATTACHMENT":
        event = {
          type: "Attachment",
          timestamp: auditEvent.timestamp,
          user: auditEvent.performedBy || "System",
          userId: auditEvent.performedById,
          performedByUserLevel: auditEvent.performedByUserLevel || "N/A",
          details: auditEvent.details?.description || "Attachment added",
          uploadDateTime: auditEvent.details?.uploadDateTime,
          uploadedBy: auditEvent.details?.uploadedBy,
          originalSaveLevel: auditEvent.originalSaveLevel,
          eventType: auditEvent.eventType,
          icon: "📎",
          color: "#6B7280",
          slaPaused: false
        };
        break;
        
      case "ESCALATION":
        let escalationDetails = `Escalation to ${auditEvent.details?.escalatedTo || "L2"}`;
        
        // Check if followed by REASSIGNMENT event
        if (isFollowedByReassignment && nextEvent.details) {
          const reassignTo = nextEvent.details.reassignmentUser || 
                           nextEvent.details.newL1User || 
                           nextEvent.details.newL2User || "User";
          escalationDetails += ` → Reassigned to ${reassignTo}`;
        }
        
        event = {
          type: "Escalation",
          timestamp: auditEvent.timestamp,
          user: auditEvent.performedBy || "System",
          performedByUserLevel: auditEvent.performedByUserLevel || "N/A",
          details: escalationDetails,
          escalationType: auditEvent.details?.escalationType,
          escalatedTo: auditEvent.details?.escalatedTo,
          remarks: auditEvent.details?.escalationRemarks,
          escalationDateTime: auditEvent.details?.escalationDateTime,
          originalSaveLevel: auditEvent.originalSaveLevel,
          eventType: auditEvent.eventType,
          icon: "⬆️",
          color: "#EC4899",
          slaPaused: false,
          slaBreached: auditEvent.details?.slaBreached || false,
          slaBreachedAt: auditEvent.details?.slaBreachedAt || null,
          originalSlaDeadline: auditEvent.details?.originalSlaDeadline || null,
          escalationReason: auditEvent.details?.escalationReason || "",
          hasReassignment: isFollowedByReassignment,
          reassignmentDetails: isFollowedByReassignment ? nextEvent.details : null
        };
        break;
        
      case "GENERIC_UPDATE":
        event = {
          type: "System Update",
          timestamp: auditEvent.timestamp,
          user: auditEvent.details?.updatedBy || "System",
          performedByUserLevel: auditEvent.performedByUserLevel || "N/A",
          details: `Ticket Updated - ${auditEvent.details?.saveLevel || "Update"}`,
          saveLevel: auditEvent.details?.saveLevel,
          updateDateTime: auditEvent.details?.updateDateTime,
          originalSaveLevel: auditEvent.originalSaveLevel,
          eventType: auditEvent.eventType,
          icon: "⚙️",
          color: "#6B7280",
          slaPaused: false
        };
        break;
        
      case "REASSIGNMENT":
        // SKIP - this will be merged with previous event
        return null;
        
      case "BULK_ASSIGNMENT":
        const assignedUserLevel = auditEvent.details?.assignedUser?.userLevel || "N/A";
        const assignedUserName = auditEvent.details?.assignedUser?.userName || "User";
        
        event = {
          type: "Bulk Assignment",
          timestamp: auditEvent.timestamp,
          user: auditEvent.performedBy || "System",
          userId: auditEvent.performedById,
          performedByUserLevel: "N/A", // Always show as Support Team Admin
          details: `Bulk assigned to ${assignedUserName} (${assignedUserLevel})`,
          assignedUser: auditEvent.details?.assignedUser,
          assignedBy: auditEvent.details?.assignedBy,
          assignmentDateTime: auditEvent.details?.assignmentDateTime,
          isAdminAssignment: auditEvent.details?.isAdminAssignment,
          originalSaveLevel: auditEvent.originalSaveLevel,
          eventType: auditEvent.eventType,
          icon: "👥",
          color: "#3B82F6",
          slaPaused: false
        };
        break;
        
      case "L1_ASSIGNMENT":
        const newL1User = auditEvent.details?.newL1User || "User";
        const previousL1User = auditEvent.details?.previousL1User || "N/A";
        
        event = {
          type: "L1 User Assignment",
          timestamp: auditEvent.timestamp,
          user: auditEvent.performedBy || "System",
          userId: auditEvent.performedById,
          performedByUserLevel: "N/A",// Always show as Support Team Admin
          details: `L1 assigned from ${previousL1User} to ${newL1User}`,
          newL1User: newL1User,
          newL1UserId: auditEvent.details?.newL1UserId,
          previousL1User: previousL1User,
          previousL1UserId: auditEvent.details?.previousL1UserId,
          reassignmentDescription: auditEvent.details?.reassignmentDescription,
          reassignmentDateTime: auditEvent.details?.reassignmentDateTime,
          reassignedBy: auditEvent.details?.reassignedBy,
          grievanceStatus: auditEvent.details?.grievanceStatus,
          originalSaveLevel: auditEvent.originalSaveLevel,
          eventType: auditEvent.eventType,
          icon: "🔄",
          color: "#10B981",
          slaPaused: false
        };
        break;
        
      case "L2_ASSIGNMENT":
        const newL2User = auditEvent.details?.newL2User || "User";
        const previousL2User = auditEvent.details?.previousL2User || "N/A";
        
        event = {
          type: "L2 User Assignment",
          timestamp: auditEvent.timestamp,
          user: auditEvent.performedBy || "System",
          userId: auditEvent.performedById,
          performedByUserLevel: "N/A", // Always show as Support Team Admin
          details: `L2 assigned from ${previousL2User} to ${newL2User}`,
          newL2User: newL2User,
          newL2UserId: auditEvent.details?.newL2UserId,
          previousL2User: previousL2User,
          previousL2UserId: auditEvent.details?.previousL2UserId,
          reassignmentDescription: auditEvent.details?.reassignmentDescription,
          reassignmentDateTime: auditEvent.details?.reassignmentDateTime,
          reassignedBy: auditEvent.details?.reassignedBy,
          grievanceStatus: auditEvent.details?.grievanceStatus,
          isAdminAssignment: auditEvent.details?.isAdminAssignment,
          adminUser: auditEvent.details?.adminUser,
          adminAssignmentTime: auditEvent.details?.adminAssignmentTime,
          originalSaveLevel: auditEvent.originalSaveLevel,
          eventType: auditEvent.eventType,
          icon: "🔄",
          color: "#8B5CF6",
          slaPaused: false
        };
        break;
        
      default:
        event = {
          type: auditEvent.event || "Event",
          timestamp: auditEvent.timestamp,
          user: auditEvent.performedBy || "System",
          userId: auditEvent.performedById,
          performedByUserLevel: auditEvent.performedByUserLevel || "N/A",
          details: auditEvent.event || "Event occurred",
          originalSaveLevel: auditEvent.originalSaveLevel,
          eventType: auditEvent.eventType,
          icon: "📝",
          color: "#6B7280",
          slaPaused: false
        };
    }
    
    return event;
  });
  
  // Filter out null events (REASSIGNMENT events that were merged)
  const filteredEvents = events.filter(event => event !== null);
  
  // ============================================================================
  // EXTRACT RESOLUTION DATE/TIME - Find the LATEST one across all events
  // ============================================================================
  
  let latestResolutionDateTime = null;

  // Loop through auditHistory from LAST to FIRST to find the most recent resolutionDateTime
  for (let i = apiResponse.auditHistory.length - 1; i >= 0; i--) {
    const auditEvent = apiResponse.auditHistory[i];

    // Check if this event has resolutionDateTime and it's not "N/A"
    if (auditEvent.details?.resolutionDateTime &&
      auditEvent.details.resolutionDateTime !== "N/A") {
      latestResolutionDateTime = auditEvent.details.resolutionDateTime;
      break; // Stop at the first (most recent) non-null value
    }

    // Also check in TICKET_CREATION events specifically
    if (auditEvent.eventType === "TICKET_CREATION" &&
      auditEvent.details?.resolutionDateTime &&
      auditEvent.details.resolutionDateTime !== "N/A") {
      latestResolutionDateTime = auditEvent.details.resolutionDateTime;
      break;
    }

    // Check in STATUS_CHANGE events
    if (auditEvent.eventType === "STATUS_CHANGE" &&
      auditEvent.details?.resolutionDateTime &&
      auditEvent.details.resolutionDateTime !== "N/A") {
      latestResolutionDateTime = auditEvent.details.resolutionDateTime;
      break;
    }
  }

  // If still not found, try one more pass through all events
  if (!latestResolutionDateTime) {
    for (let i = apiResponse.auditHistory.length - 1; i >= 0; i--) {
      const auditEvent = apiResponse.auditHistory[i];
      if (auditEvent.details?.resolutionDateTime) {
        latestResolutionDateTime = auditEvent.details.resolutionDateTime;
        break;
      }
    }
  }

  
  const firstEvent = apiResponse.auditHistory[0];
  const firstEventDetails = firstEvent?.details || {};
  const slaSummary = apiResponse.summary || {};
  
  const escalationEvents = filteredEvents.filter(event => event.type === "Escalation");
  const slaBreachEvent = escalationEvents.find(event => event.slaBreached === true);
  
  const slaPausedEvents = filteredEvents.filter(event => event.slaPaused === true);
  
  const lastStatusEvent = filteredEvents
    .filter(event => event.type === "Status Change")
    .pop();
  const currentSlaStatus = lastStatusEvent?.slaPaused === true ? "Paused" : "Active";
  
  return {
    id: apiResponse.grievanceSeqId,
    ticketId: apiResponse.ticketId,
    subject: apiResponse.subject,
    bank: firstEventDetails.bankName || "N/A",
    branch: firstEventDetails.branchName || "N/A",
    date: formatTimestamp(apiResponse.auditHistory[0]?.timestamp),
    status: apiResponse.summary.currentStatus,
    priority: firstEventDetails.priority || "N/A",
    requesterBy: firstEventDetails.reportedBy || "N/A",
    handler: firstEventDetails.supportTeam || "N/A",
    assignToUserName: apiResponse.summary.currentAssignedTo || "N/A",
    auditCount: apiResponse.totalEntries,
    auditEvents: filteredEvents.sort((a, b) => a.timestamp - b.timestamp),
    summary: apiResponse.summary,
    createdOn: apiResponse.auditHistory[0]?.timestamp,
    ticketCreatedAt: apiResponse.summary.ticketCreatedAt,
    issueDescription: apiResponse.subject,
    category: firstEventDetails.category,
    subcategory: firstEventDetails.subcategory,
    phoneNumber: firstEventDetails.requestorContactNumber,
    requestorEmail: firstEventDetails.requestorEmail,
    preferredResolutionMethod: firstEventDetails.preferredResolutionMethod,
    escalationLevel: firstEventDetails.escalationLevel,
    supportTeam: firstEventDetails.supportTeam,
    resolutionDateTime: latestResolutionDateTime || "N/A",
    parentGrievanceTicketId: firstEventDetails.parentGrievanceTicketId || "N/A",
    slaMetrics: {
      slaPausedCount: slaSummary.slaPausedCount || slaPausedEvents.length,
      totalStatusChanges: slaSummary.totalStatusChanges || 0,
      currentSlaStatus: currentSlaStatus,
      ticketCreatedAt: slaSummary.ticketCreatedAt,
      slaBreached: slaBreachEvent ? true : false,
      slaBreachedAt: slaBreachEvent?.slaBreachedAt || null,
      slaBreachReason: slaBreachEvent?.escalationReason || null,
      slaPausedEvents: slaPausedEvents.length,
      slaResumedEvents: filteredEvents.filter(event => 
        event.slaPaused === false && 
        event.eventType === "STATUS_CHANGE" &&
        filteredEvents.find(e => 
          e.timestamp < event.timestamp && 
          e.slaPaused === true && 
          e.eventType === "STATUS_CHANGE"
        )
      ).length
    },
    originalData: apiResponse
  };
};



// ============================================================================
// EXPLANATION OF RESOLUTION DATE/TIME EXTRACTION:
// ============================================================================
// 
// Based on your API responses:
// 
// Example 1 (Ticket 11122026021200084):
// - TICKET_CREATION event has: "resolutionDateTime": "13-02-2026 11:53:00 PM"
// - STATUS_CHANGE event has: "resolutionDateTime": "13-02-2026 11:53:00 PM"
// 
// Example 2 (Ticket 11122026021200079):
// - TICKET_CREATION event has: "resolutionDateTime": "13-02-2026 11:38:00 PM"
// - No STATUS_CHANGE events have resolutionDateTime
// 
// The function now:
// 1. Loops through ALL auditHistory events
// 2. Checks each event's details.resolutionDateTime
// 3. Stores the LATEST resolutionDateTime found
// 4. Sets it at the ticket level for easy access in exports
// 
// This ensures that:
// - If multiple events have resolutionDateTime, we get the most recent one
// - If only TICKET_CREATION has it, we use that
// - If none have it, we show "N/A"
// 
// The resolution date/time is then available as:
// - ticket.resolutionDateTime in all export functions

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "N/A";
    
    if (typeof timestamp === 'number') {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return "Invalid Date";
      
      const dd = String(date.getDate()).padStart(2, "0");
      const mm = String(date.getMonth() + 1).padStart(2, "0");
      const yyyy = date.getFullYear();
      
      let hours = date.getHours();
      const minutes = String(date.getMinutes()).padStart(2, "0");
      const ampm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12 || 12;
      const formattedHours = String(hours).padStart(2, "0");

      return `${dd}-${mm}-${yyyy} ${formattedHours}:${minutes} ${ampm}`;
    }
    
    return timestamp;
  };

  const formatTimestampForExport = (timestamp) => {
    if (!timestamp) return "N/A";

    if (typeof timestamp === 'number') {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return "Invalid Date";

      const dd = String(date.getDate()).padStart(2, "0");
      const mm = String(date.getMonth() + 1).padStart(2, "0");
      const yyyy = date.getFullYear();

      let hours = date.getHours();
      const minutes = String(date.getMinutes()).padStart(2, "0");
      const seconds = String(date.getSeconds()).padStart(2, "0");
      const ampm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12 || 12;
      const formattedHours = String(hours).padStart(2, "0");

      return `${dd}-${mm}-${yyyy} ${formattedHours}:${minutes}:${seconds} ${ampm}`;
    }

    return timestamp;
  };

  // Add these helper functions at the top of the AuditHistory component, after the imports

// ===== AGEING CALCULATION HELPERS =====
const formatDuration = (ms) => {
  if (ms <= 0) return "0m";
  const totalMinutes = Math.floor(ms / 60000);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;

  let parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0 || parts.length === 0) parts.push(`${minutes}m`);

  return parts.join(" ");
};

  // Add this helper function after createGrievancePayload function
const createAuditHistoryPayload = (userProfile) => {
  const {
    role_name,
    bank_code,
    id: userId,
    is_admin,
    user_level,
  } = userProfile;

  console.log("Creating audit history payload for user:", { role_name, is_admin, user_level });

  // SUPER ADMIN role
  if (role_name === "SUPER ADMIN") {
    return {
      admin_role: "SA",
      bank_code: null,
      branch_code: null,
      userId: userId || null,
      userLevel: user_level || null,
    };
  }

  // HO role
  if (is_admin === true) {
    return {
      admin_role: "HO",
        bank_code: bank_code || null,
        branch_code: null,
        userId: userId || null,
        userLevel: user_level || null,
    };
  }

  // Default fallback
  return {
    admin_role: "USER",
      bank_code: null,
      branch_code: null,
      userId: userId || null,
      userLevel: user_level || null,
  };
};

// ===== AGEING CALCULATION FOR AUDIT HISTORY DATA =====
const calculateAgeingFromAuditHistory = (auditHistoryData) => {
  if (!auditHistoryData || !auditHistoryData.auditHistory) {
    return { resolvedAgeing: "N/A", closedAgeing: "N/A" };
  }

  // Find the ticket creation event
  const creationEvent = auditHistoryData.auditHistory.find(
    event => event.eventType === "TICKET_CREATION"
  );
  
  if (!creationEvent) {
    return { resolvedAgeing: "N/A", closedAgeing: "N/A" };
  }

  const createdOn = new Date(creationEvent.timestamp);
  if (isNaN(createdOn)) {
    return { resolvedAgeing: "N/A", closedAgeing: "N/A" };
  }

  // Get all status change events sorted by timestamp
  const statusChangeEvents = auditHistoryData.auditHistory
    .filter(event => event.eventType === "STATUS_CHANGE")
    .sort((a, b) => a.timestamp - b.timestamp);

  // Find Resolved and Closed timestamps
  let resolvedDate = null;
  let closedDate = null;

  for (const event of statusChangeEvents) {
    const newStatus = event.details?.newStatus;
    
    if (newStatus === "Resolved" && !resolvedDate) {
      resolvedDate = new Date(event.timestamp);
    }
    
    if (newStatus === "Closed" && !closedDate) {
      closedDate = new Date(event.timestamp);
    }
  }

  // Calculate total pending time (SLA Paused periods)
  let totalPendingMs = 0;
  let currentPendingStart = null;

  for (const event of statusChangeEvents) {
    // eslint-disable-next-line no-unused-vars
    const newStatus = event.details?.newStatus;
    const slaPaused = event.details?.slaPaused;

    // Check if entering a pending status (SLA Paused)
    if (slaPaused === true && !currentPendingStart) {
      currentPendingStart = new Date(event.timestamp);
    }
    
    // Check if exiting a pending status (SLA Resumed)
    if (slaPaused === false && currentPendingStart) {
      const pendingEnd = new Date(event.timestamp);
      totalPendingMs += pendingEnd - currentPendingStart;
      currentPendingStart = null;
    }
  }

  // Calculate ageings
  let resolvedAgeing = "N/A";
  let closedAgeing = "N/A";

  if (resolvedDate && !isNaN(resolvedDate)) {
    const ageingMs = resolvedDate - createdOn - totalPendingMs;
    resolvedAgeing = formatDuration(Math.max(0, ageingMs));
  }

  if (closedDate && !isNaN(closedDate)) {
    const ageingMs = closedDate - createdOn - totalPendingMs;
    closedAgeing = formatDuration(Math.max(0, ageingMs));
  }

  return { resolvedAgeing, closedAgeing };
};
// ===== END AGEING CALCULATION FOR AUDIT HISTORY =====

const handleExportAllToExcel = async () => {
  setExporting(true);
  try {
     // Get user profile to determine role and construct payload
    const userProfile = await getData(getThemeURL);
    
    if (!userProfile || !Array.isArray(userProfile) || userProfile.length === 0) {
      toast.error("Failed to fetch user profile");
      setExporting(false);
      return;
    }

    const user = userProfile[0];
    
    // ====== THIS IS THE KEY CHANGE - CREATE AUDIT HISTORY PAYLOAD ======
    const payload = createAuditHistoryPayload(user);
    
    console.log("Audit History Payload:", payload);
    
    // ====== USE postDataWithBody INSTEAD OF getData ======
    const apiResponse = await postDataWithBody(getAllAuditHistory, payload, {});
    console.log("Export all to Excel", apiResponse);

    const wb = XLSX.utils.book_new();
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);

    const applyBoldStyle = (worksheet, cellRefs) => {
      cellRefs.forEach(ref => {
        if (worksheet[ref]) {
          worksheet[ref].s = { font: { bold: true } };
        }
      });
    };

   // =============== SHEET 1: OVERALL SUMMARY ===============
const totalTickets = apiResponse.data.length;

// Process first ticket for summary if available
let firstTicketSummary = null;
if (apiResponse.data.length > 0) {
  // eslint-disable-next-line no-unused-vars
  firstTicketSummary = processAuditHistoryData(apiResponse.data[0]);
}

// Define the EXACT status order as shown in your requirement
const statusOrder = [
  "Open",
  "In-Progress", 
  "Issue Pending at User Level",
  "Issue Pending at External Vendor",  // This must come before Resolved
  "Resolved",
  "Closed"
];

const statusBreakdown = {};

apiResponse.data.forEach(ticketData => {
  const ticket = processAuditHistoryData(ticketData);
  const status = ticket.status;
  statusBreakdown[status] = (statusBreakdown[status] || 0) + 1;
});

// Fixed summary data section
const summaryData = [
  ["AUDIT HISTORY REPORT - COMPREHENSIVE ANALYSIS"],
  ["Bulk Ticket Tracking & Analysis Report"],
  ["REPORT INFORMATION"],
  ["Report Generated:", new Date().toLocaleDateString('en-GB')],
  ["Total Tickets Analyzed:", totalTickets],
  ["STATUS DISTRIBUTION"],
  ["Status", "Count"]
];

// Add status breakdown rows in the CORRECT order
statusOrder.forEach(status => {
  if (statusBreakdown[status]) {
    summaryData.push([status, statusBreakdown[status]]);
  }
});

// Add any other statuses that might exist (like "Issues Pending at External Vendor" vs "Issue Pending at External Vendor")
Object.entries(statusBreakdown).forEach(([status, count]) => {
  if (!statusOrder.includes(status)) {
    // Check if it's a variation of the external vendor status
    if (status.toLowerCase().includes("external vendor")) {
      // Insert it before Resolved
      const resolvedIndex = summaryData.findIndex(row => row[0] === "Resolved");
      if (resolvedIndex !== -1) {
        summaryData.splice(resolvedIndex, 0, [status, count]);
      } else {
        summaryData.push([status, count]);
      }
    } else {
      summaryData.push([status, count]);
    }
  }
});

    // Priority Breakdown
    const priorityBreakdown = {};
    apiResponse.data.forEach(ticketData => {
      const ticket = processAuditHistoryData(ticketData);
      const priority = ticket.priority || "N/A";
      priorityBreakdown[priority] = (priorityBreakdown[priority] || 0) + 1;
    });

    summaryData.push([""]);
    summaryData.push(["PRIORITY DISTRIBUTION"]);
    summaryData.push(["Priority", "Count"]);
    Object.entries(priorityBreakdown).forEach(([priority, count]) => {
      summaryData.push([priority, count]);
    });

const ws1 = XLSX.utils.aoa_to_sheet(summaryData);
ws1['!cols'] = [{ wch: 30 }, { wch: 25 }, { wch: 15 }];

// Rest of your Excel code...

    applyBoldStyle(ws1, [
      'A1', 'A2', 'A4', 'A11', 'A17', 'A22',
      ...summaryData.slice(4, 10).map((_, i) => `A${5 + i}`),
      ...summaryData.slice(11, 16).map((_, i) => `A${12 + i}`),
      'A18', 'B18', 'C18'
    ]);

    XLSX.utils.book_append_sheet(wb, ws1, "Overall Summary");

    // =============== SHEET 2: ALL TICKETS OVERVIEW ===============
    const ticketsOverviewData = [
      ["ALL TICKETS OVERVIEW"],
      ["Summary of all tickets with key metrics"],
      [""],
      ["S.No", "Ticket ID", "Subject", "Status", "Priority", "Bank", "Branch", "Ticket Created Date & Time","Resolved Ageing", "Closed Ageing"]
    ];

    apiResponse.data.forEach((ticketData, index) => {
      const ticket = processAuditHistoryData(ticketData);
      // const statusChanges = ticket.auditEvents.filter(e => e.type === "Status Change").length;

     const ageingData = calculateAgeingFromAuditHistory(ticketData);

      ticketsOverviewData.push([
        index + 1,
        ticket.id,
        ticket.subject,
        ticket.status,
        ticket.priority,
        ticket.bank,
        ticket.branch,
        ticket.auditEvents[0]?.ticketCreatedDateTime,
        ageingData.resolvedAgeing,  // NEW
    ageingData.closedAgeing      
      ]);
    });

    const ws2 = XLSX.utils.aoa_to_sheet(ticketsOverviewData);
    ws2['!cols'] = [
      { wch: 5 },
      { wch: 12 },
      { wch: 40 },
      { wch: 15 },
      { wch: 10 },
      { wch: 20 },
      { wch: 20 },
      { wch: 20 },
       { wch: 15 },  // Resolved Ageing
  { wch: 15 },
    ];

    applyBoldStyle(ws2, [
      'A1', 'A2',
      'A4', 'B4', 'C4', 'D4', 'E4', 'F4',
      'G4','I4', 'J4'
    ]);

    XLSX.utils.book_append_sheet(wb, ws2, "All Tickets Overview");

    // =============== SHEET 3+: INDIVIDUAL TICKET DETAILS ===============
    apiResponse.data.forEach((ticketData, ticketIndex) => {
      const ticket = processAuditHistoryData(ticketData);

      // if (ticketIndex < 20) {
        const ticketDetailData = [
          [`TICKET #${ticket.id} - DETAILED TIMELINE`],
          [""],
          ["TICKET INFORMATION"],
          ["Subject:", ticket.subject],
          ["Status:", ticket.status],
          ["Priority:", ticket.priority],
          ["Bank:", ticket.bank],
          ["Branch:", ticket.branch],
          ["Ticket Created Date & Time", ticket.auditEvents[0]?.ticketCreatedDateTime || "N/A"],
          ["Parent Ticket ID:", ticket.parentGrievanceTicketId],
          ["Resolution Date & Time:", ticket.resolutionDateTime || "N/A"], // NEW LINE
          [""],
          ["COMPLETE TIMELINE"],
          ["S.No", "Event Date & Time", "Event Type", "Performed By", "Action"]
        ];

        // Filter out Comment Added and Attachment events
        const filteredEvents = ticket.auditEvents.filter(event => 
          event.type !== "Comment Added" && event.type !== "Attachment"
        );

        filteredEvents.forEach((event, eventIndex) => {
          // In the Excel generation sections (both handleExportAllToExcel and exportIndividualToExcel),
// update the actionDetails for Status Change events:
let actionDetails = "";

switch (event.type) {
  case "Ticket Creation":
    actionDetails = event.details; // This now includes assignment if present
    break;
    
  case "Status Change":
    let statusChangeDetails = `${event.previous || "N/A"} → ${event.current || "N/A"}`;
    
    // Helper function for pending status check
    const isPendingStatus = (status) => {
      if (!status) return false;
      const statusLower = status.toLowerCase();
      return statusLower.includes("issue pending at user level") || 
             statusLower.includes("issue pending at user") ||
             statusLower.includes("issue pending at external vendor") ||
             statusLower.includes("issue pending at external") ||
             statusLower.includes("pending at user") ||
             statusLower.includes("pending at external");
    };
    
    // Check for SLA Paused
    if (isPendingStatus(event.current)) {
      statusChangeDetails += " [SLA Paused]";
    }
    
    // Check for SLA Resumed
    if (isPendingStatus(event.previous) && !isPendingStatus(event.current)) {
      statusChangeDetails += " [SLA Resumed]";
    }
    
    // Add reassignment info if present
    if (event.hasReassignment && event.reassignmentDetails) {
      const reassignTo = event.reassignmentDetails.reassignmentUser || 
                        event.reassignmentDetails.newL1User || 
                        event.reassignmentDetails.newL2User || "User";
      statusChangeDetails += ` → Reassigned to ${reassignTo}`;
    }
    
    actionDetails = statusChangeDetails;
    break;
    
  case "Escalation":
    let escalationDetails = event.escalationType === "Manual"
      ? `Manually Escalated to ${event.escalatedTo || "N/A"}`
      : `Escalated to ${event.escalatedTo || "N/A"}`;
    
    // SLA Breached check
    let isSlaBreached = false;
    if (event.escalationReason && event.escalationReason.toLowerCase().includes("sla")) {
      isSlaBreached = true;
      escalationDetails += ` - ${event.escalationReason}`;
    }
    if (ticket.slaMetrics && ticket.slaMetrics.slaBreached && ticket.slaMetrics.slaBreachedAt) {
      const escalationTime = new Date(event.timestamp).getTime();
      const breachTime = new Date(ticket.slaMetrics.slaBreachedAt).getTime();
      const timeDiffMinutes = Math.abs(escalationTime - breachTime) / (1000 * 60);
      if (timeDiffMinutes < 5) {
        isSlaBreached = true;
        escalationDetails += ` [SLA Breached - ${ticket.slaMetrics.slaBreachReason || "SLA time exceeded"}]`;
      }
    }
    if (event.escalationType && event.escalationType.toLowerCase().includes("sla")) {
      isSlaBreached = true;
      escalationDetails += ` [SLA Breached - ${event.escalationReason || "SLA threshold exceeded"}]`;
    }
    if (isSlaBreached && !escalationDetails.includes("[SLA Breached]")) {
      escalationDetails += ` [SLA Breached]`;
    }
    
    // Add reassignment info if present
    if (event.hasReassignment && event.reassignmentDetails) {
      const reassignTo = event.reassignmentDetails.reassignmentUser || 
                        event.reassignmentDetails.newL1User || 
                        event.reassignmentDetails.newL2User || "User";
      escalationDetails += ` → Reassigned to ${reassignTo}`;
    }
    
    actionDetails = escalationDetails;
    break;
    
  case "Support Team Admin Bulk Assign":
  case "Bulk Assignment":
  case "L1 User Reassignment":
  case "L1 User Assignment":
  case "L2 User Reassignment":
  case "L2 User Assignment":
    actionDetails = event.details;
    break;
    
  case "System Update":
    actionDetails = "System Update";
    break;
    
  default:
    actionDetails = event.type;
}

          ticketDetailData.push([
            eventIndex + 1,
            event.type === "Ticket Creation"
          ? (event.ticketCreatedDateTime || "N/A")
          : formatTimestampForExport(event.timestamp),
            event.type,
            event.eventType === "L1_ASSIGNMENT"
            ? `${event.user} (HO)`
            : event.eventType === "L2_ASSIGNMENT"
            ? `${event.user} (Support Team Admin)`
            : event.eventType === "BULK_ASSIGNMENT"
            ? `${event.user} (Support Team Admin)`
            : event.eventType === "ESCALATION" && event.escalationType === "Auto"
            ? `System (Auto)`
            : `${event.user} (${event.performedByUserLevel || "N/A"})`,
            actionDetails,
          ]);
        });

        const wsTicket = XLSX.utils.aoa_to_sheet(ticketDetailData);
        wsTicket['!cols'] = [
          { wch: 5 },
          { wch: 20 },
          { wch: 18 },
          { wch: 18 },
          { wch: 12 },
          { wch: 45 },
        ];

        applyBoldStyle(wsTicket, [
          'A1', 'A3', 'A12',
          'B13', 'C13', 'D13', 'E13', 'F13',
        ]);

        const sheetName = `Ticket ${ticket.id}`.substring(0, 31);
        XLSX.utils.book_append_sheet(wb, wsTicket, sheetName);
      // }
    });

    // Save the Excel file
    const fileName = `Bulk_Audit_History_${totalTickets}_Tickets_${timestamp}.xlsx`;
    XLSX.writeFile(wb, fileName);

    toast.success(`Bulk audit history exported successfully! (${totalTickets} tickets)`);

  } catch (error) {
    console.error("Error exporting all to Excel:", error);
    toast.error("Failed to export bulk data to Excel. Please try again.");
  } finally {
    setExporting(false);
  }
};

const handleExportAllToPDF = async () => {
  setExporting(true);
  try {
    // Get user profile to determine role and construct payload
    const userProfile = await getData(getThemeURL);
    
    if (!userProfile || !Array.isArray(userProfile) || userProfile.length === 0) {
      toast.error("Failed to fetch user profile");
      setExporting(false);
      return;
    }

    const user = userProfile[0];
    
    // ====== THIS IS THE KEY CHANGE - CREATE AUDIT HISTORY PAYLOAD ======
    const payload = createAuditHistoryPayload(user);
    
    console.log("Audit History Payload:", payload);
    
    // ====== USE postDataWithBody INSTEAD OF getData ======
    const apiResponse = await postDataWithBody(getAllAuditHistory, payload, {});
    console.log("Export all to PDF", apiResponse);

    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    doc.setFont("helvetica");

    const colors = {
      darkBlue: [0, 51, 102],
      blue: [59, 130, 246],
      lightBlue: [219, 234, 254],
      green: [16, 185, 129],
      lightGreen: [220, 252, 231],
      orange: [251, 146, 60],
      lightOrange: [255, 237, 213],
      red: [239, 68, 68],
      lightRed: [254, 226, 226],
      gray: [107, 114, 128],
      lightGray: [243, 244, 246],
      purple: [139, 92, 246],
      lightPurple: [237, 233, 254],
      pink: [236, 72, 153],
      lightPink: [252, 231, 243],
      teal: [20, 184, 166],
      lightTeal: [204, 251, 241],
      indigo: [99, 102, 241],
      lightIndigo: [224, 231, 255]
    };

    const leftMargin = 15;
    const rightMargin = 195;
    const pageWidth = 210;
    const pageHeight = 297;
    let pageNumber = 1;

    // Calculate overall statistics
    const totalTickets = apiResponse.data.length;
    
    // Define the EXACT status order
    const statusOrder = [
      "Open",
      "In-Progress",
      "Issue Pending at User Level",
      "Issue Pending at External Vendor",
      "Resolved",
      "Closed"
    ];
    
    const statusBreakdown = {};
    const priorityBreakdown = {};
    const bankBreakdown = {};
    
    // Calculate meaningful business metrics across all tickets
    // eslint-disable-next-line no-unused-vars
    let totalEscalations = 0;
    // eslint-disable-next-line no-unused-vars
    let totalSystemUpdates = 0;
    // eslint-disable-next-line no-unused-vars
    let totalSLAPaused = 0;
    // eslint-disable-next-line no-unused-vars
    let totalSLABreached = 0;
    
    // Time-based metrics
    // eslint-disable-next-line no-unused-vars
    let totalTicketAge = 0;
    // eslint-disable-next-line no-unused-vars
    let ticketsWithResolution = 0;
    const now = new Date();
    const uniqueUsersSet = new Set();
    const uniqueBanksSet = new Set();
    const uniqueBranchesSet = new Set();
    const uniqueCategoriesSet = new Set();
    
    // High priority count
    // eslint-disable-next-line no-unused-vars
    let highPriorityCount = 0;
    
    // Status completion rate
    // eslint-disable-next-line no-unused-vars
    let completedTickets = 0;
    // eslint-disable-next-line no-unused-vars
    let inProgressTickets = 0;
    // eslint-disable-next-line no-unused-vars
    let pendingTickets = 0;
    
    apiResponse.data.forEach(ticketData => {
      const ticket = processAuditHistoryData(ticketData);
      const status = ticket.status;
      statusBreakdown[status] = (statusBreakdown[status] || 0) + 1;
      
      // Get priority
      const priority = ticket.priority || "N/A";
      priorityBreakdown[priority] = (priorityBreakdown[priority] || 0) + 1;
      
      // Check for high priority
      if (priority.toLowerCase().includes("high") || priority.toLowerCase().includes("urgent") || priority === "1" || priority === "2") {
        highPriorityCount++;
      }
      
      // Get bank name
      const bankName = ticket.bank || "N/A";
      bankBreakdown[bankName] = (bankBreakdown[bankName] || 0) + 1;
      
      // Track status completion
      if (status.toLowerCase().includes("complete") || status.toLowerCase().includes("resolved") || status.toLowerCase().includes("closed")) {
        completedTickets++;
      } else if (status.toLowerCase().includes("progress") || status.toLowerCase().includes("working")) {
        inProgressTickets++;
      } else if (status.toLowerCase().includes("pending") || status.toLowerCase().includes("open")) {
        pendingTickets++;
      }
      
      // Calculate metrics
      ticket.auditEvents.forEach(event => {
        if (event.userId) uniqueUsersSet.add(event.userId);
        
        if (event.type === "Escalation") totalEscalations++;
        if (event.type === "System Update") totalSystemUpdates++;
        
        // Track SLA Paused from Status Change events
        if (event.type === "Status Change" && event.current) {
          if (event.current.toLowerCase().includes("issue pending at user level") || 
              event.current.toLowerCase().includes("issue pending at external Vendor")) {
            totalSLAPaused++;
          }
        }
        
        // Track SLA Breached from Escalation events
        if (event.type === "Escalation") {
          if (event.escalationReason && event.escalationReason.toLowerCase().includes("sla")) {
            totalSLABreached++;
          }
        }
      });
      
      // Track unique entities
      if (ticket.bank) uniqueBanksSet.add(ticket.bank);
      if (ticket.branch) uniqueBranchesSet.add(ticket.branch);
      if (ticket.category) uniqueCategoriesSet.add(ticket.category);
      
      // Calculate ticket age
      if (ticket.createdOn) {
        const createdDate = new Date(ticket.createdOn);
        const ageInDays = Math.floor((now - createdDate) / (1000 * 60 * 60 * 24));
        totalTicketAge += ageInDays;
        
        // Check if resolved
        if (ticket.status && (ticket.status.toLowerCase().includes("resolved") || 
            ticket.status.toLowerCase().includes("completed") || 
            ticket.status.toLowerCase().includes("closed"))) {
          ticketsWithResolution++;
        }
      }
    });

    // const uniqueUsers = uniqueUsersSet.size;
    // const uniqueBanks = uniqueBanksSet.size;
    // const uniqueBranches = uniqueBranchesSet.size;
    // const uniqueCategories = uniqueCategoriesSet.size;
    // const avgTicketAge = totalTickets > 0 ? (totalTicketAge / totalTickets).toFixed(1) : 0;
    // const resolutionRate = totalTickets > 0 ? ((completedTickets / totalTickets) * 100).toFixed(1) : 0;
    // const escalationRate = totalTickets > 0 ? ((totalEscalations / totalTickets) * 100).toFixed(1) : 0;
    // const slaPausedRate = totalTickets > 0 ? ((totalSLAPaused / totalTickets) * 100).toFixed(1) : 0;
    // const slaBreachedRate = totalTickets > 0 ? ((totalSLABreached / totalTickets) * 100).toFixed(1) : 0;

    // ============ PAGE 1: EXECUTIVE SUMMARY ============
    // Top Header Bar
    doc.setFillColor(...colors.darkBlue);
    doc.rect(0, 0, pageWidth, 45, 'F');

    // White Title
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.setFont(undefined, 'bold');
    doc.text("BULK AUDIT HISTORY REPORT", pageWidth / 2, 22, { align: 'center' });
    
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text("Complete System Overview & Analysis", pageWidth / 2, 32, { align: 'center' });

    let yPos = 55;

    // Report Info Box
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(leftMargin, yPos, rightMargin - leftMargin, 25, 2, 2, 'F');
    
    doc.setTextColor(71, 85, 105);
    doc.setFontSize(8.5);
    doc.setFont(undefined, 'normal');
    
    const currentDate = new Date();
    const formattedDate = `${currentDate.getDate().toString().padStart(2, '0')}-${(currentDate.getMonth() + 1).toString().padStart(2, '0')}-${currentDate.getFullYear()}`;
    
    doc.text(`Report Generated: ${formattedDate}`, leftMargin + 5, yPos + 8);
    doc.text(`Total Tickets Analyzed: ${totalTickets}`, leftMargin + 5, yPos + 14);
    doc.text(`Report Type: Bulk Audit History`, rightMargin - 50, yPos + 14);

    yPos += 32;

    // ============ STATUS BREAKDOWN ============
    doc.setTextColor(...colors.darkBlue);
    doc.setFontSize(11);
    doc.setFont(undefined, 'bold');
    doc.text("STATUS BREAKDOWN", leftMargin, yPos);
    yPos += 8;

    // Create status table data in the CORRECT order
    const statusTableData = [];

    // First add statuses in the predefined order
    statusOrder.forEach(status => {
      if (statusBreakdown[status]) {
        statusTableData.push([status, statusBreakdown[status].toString()]);
      }
    });

    // Handle any variations of status names (like plural/singular)
    Object.entries(statusBreakdown).forEach(([status, count]) => {
      if (!statusOrder.includes(status)) {
        // Check if it's a variation of the external vendor status
        if (status.toLowerCase().includes("external vendor")) {
          // Insert it before Resolved
          const resolvedIndex = statusTableData.findIndex(row => row[0] === "Resolved");
          if (resolvedIndex !== -1) {
            statusTableData.splice(resolvedIndex, 0, [status, count.toString()]);
          } else {
            statusTableData.push([status, count.toString()]);
          }
        }
        // Check if it's a variation of the user level status
        else if (status.toLowerCase().includes("user level")) {
          // Insert it after In-Progress but before External Vendor
          const inProgressIndex = statusTableData.findIndex(row => row[0] === "In-Progress");
          const externalVendorIndex = statusTableData.findIndex(row => row[0] && row[0].toLowerCase().includes("external vendor"));
          if (externalVendorIndex !== -1) {
            statusTableData.splice(externalVendorIndex, 0, [status, count.toString()]);
          } else if (inProgressIndex !== -1) {
            statusTableData.splice(inProgressIndex + 1, 0, [status, count.toString()]);
          } else {
            statusTableData.push([status, count.toString()]);
          }
        }
        // Check if it's a variation of In-Progress
        else if (status.toLowerCase().includes("progress") || status.toLowerCase().includes("in progress")) {
          const openIndex = statusTableData.findIndex(row => row[0] === "Open");
          if (openIndex !== -1) {
            statusTableData.splice(openIndex + 1, 0, ["In-Progress", count.toString()]);
          } else {
            statusTableData.push(["In-Progress", count.toString()]);
          }
        }
        // Add any other statuses at the end
        else {
          statusTableData.push([status, count.toString()]);
        }
      }
    });

    // Remove duplicates if any
    const uniqueStatusData = [];
    const seenStatuses = new Set();
    statusTableData.forEach(([status, count]) => {
      if (!seenStatuses.has(status)) {
        seenStatuses.add(status);
        uniqueStatusData.push([status, count]);
      }
    });

    doc.autoTable({
      startY: yPos,
      head: [["Status", "Count"]],
      body: uniqueStatusData,
      margin: { left: leftMargin, right: leftMargin },
      styles: {
        fontSize: 8.5,
        cellPadding: 2.5,
        lineColor: [226, 232, 240],
        lineWidth: 0.2,
        textColor: [30, 41, 59]
      },
      headStyles: {
        fillColor: [...colors.darkBlue],
        textColor: [255, 255, 255],
        fontSize: 9,
        fontStyle: 'bold'
      },
      columnStyles: {
        0: { cellWidth: 100, fontStyle: 'bold' },
        1: { cellWidth: 40, halign: 'center' },
      }
    });

    yPos = doc.lastAutoTable.finalY + 12;

    // ============ PRIORITY BREAKDOWN ============
    doc.setTextColor(...colors.darkBlue);
    doc.setFontSize(11);
    doc.setFont(undefined, 'bold');
    doc.text("PRIORITY BREAKDOWN", leftMargin, yPos);
    yPos += 8;

    const priorityTableData = Object.entries(priorityBreakdown).map(([priority, count]) => {
      return [priority, count.toString()];
    });

    doc.autoTable({
      startY: yPos,
      head: [["Priority", "Count"]],
      body: priorityTableData,
      margin: { left: leftMargin, right: leftMargin },
      styles: {
        fontSize: 8.5,
        cellPadding: 2.5,
        lineColor: [226, 232, 240],
        lineWidth: 0.2,
        textColor: [30, 41, 59]
      },
      headStyles: {
        fillColor: [...colors.darkBlue],
        textColor: [255, 255, 255],
        fontSize: 9,
        fontStyle: 'bold'
      },
      columnStyles: {
        0: { cellWidth: 100, fontStyle: 'bold' },
        1: { cellWidth: 40, halign: 'center' },
      }
    });

    yPos = doc.lastAutoTable.finalY + 12;


    // Page Footer
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.3);
    doc.line(leftMargin, pageHeight - 20, rightMargin, pageHeight - 20);
    doc.setTextColor(148, 163, 184);
    doc.setFontSize(8);
    doc.text("Bulk Audit Report - Confidential", leftMargin, pageHeight - 12);
    doc.text(`Generated: ${formattedDate}`, pageWidth / 2, pageHeight - 12, { align: 'center' });
    doc.text(`Page ${pageNumber}`, rightMargin, pageHeight - 12, { align: 'right' });
    pageNumber++;

    // Helper function to calculate aging
    // eslint-disable-next-line no-unused-vars
    const calculateAgeing = (currentTimestamp, previousTimestamp) => {
      if (!previousTimestamp) return "Start";
      const diff = currentTimestamp - previousTimestamp;
      const seconds = Math.floor(diff / 1000);
      const minutes = Math.floor(seconds / 60);
      const hours = Math.floor(minutes / 60);
      const days = Math.floor(hours / 24);

      if (days > 0) {
        const remainingHours = hours % 24;
        return `${days}d ${remainingHours}h`;
      } else if (hours > 0) {
        const remainingMinutes = minutes % 60;
        return `${hours}h ${remainingMinutes}m`;
      } else if (minutes > 0) {
        return `${minutes}m`;
      } else {
        return `${seconds}s`;
      }
    };

    // ============ INDIVIDUAL TICKET DETAIL PAGES ============
    apiResponse.data.forEach((ticketData, ticketIndex) => {
      const ticket = processAuditHistoryData(ticketData);
      
      doc.addPage();
    const ageingData = calculateAgeingFromAuditHistory(ticketData); // or apiResponse for individual


      // ============ TICKET DETAILS PAGE ============
      yPos = 20;

      doc.setFillColor(...colors.darkBlue);
      doc.rect(0, 0, pageWidth, 35, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.setFont(undefined, 'bold');
      doc.text("TICKET DETAILS", leftMargin, 18);
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.text(`Ticket #${ticket.id}`, leftMargin, 28);

      yPos = 50;

      doc.autoTable({
        startY: yPos,
        head: [["Field", "Value"]],
        body: [
          ["Ticket ID", ticket.id],
          ["Ticket Created Date & Time", ticket.auditEvents[0]?.ticketCreatedDateTime || "N/A"],
          ["Subject", ticket.subject],
          ["", ""],
          ["Current Status", ticket.status],
          ["Priority", ticket.priority],
          ["Category", ticket.category || "N/A"],
          ["Subcategory", ticket.subcategory || "N/A"],
          ["", ""],
          ["Bank Name", ticket.bank],
          ["Branch Name", ticket.branch],
          ["", ""],
          ["Support Team", ticket.supportTeam || "N/A"],
          ["Escalation Level", ticket.escalationLevel || "N/A"],
          ["Parent Ticket ID", ticket.parentGrievanceTicketId],
          ["Resolution Date & Time", ticket.resolutionDateTime || "N/A"], // NEW LINE
          ["Resolved Ageing", ageingData.resolvedAgeing],  // NEW
      ["Closed Ageing", ageingData.closedAgeing],      // NEW
        ],
        margin: { left: leftMargin, right: leftMargin },
        styles: {
          fontSize: 8.5,
          cellPadding: 2.5,
          lineColor: [226, 232, 240],
          lineWidth: 0.2,
          textColor: [30, 41, 59]
        },
        headStyles: {
          fillColor: [...colors.darkBlue],
          textColor: [255, 255, 255],
          fontSize: 9,
          fontStyle: 'bold'
        },
        columnStyles: {
          0: { cellWidth: 50, fontStyle: 'bold', fillColor: [249, 250, 251], textColor: [...colors.gray] },
          1: { cellWidth: 130 }
        },
        didParseCell: function(data) {
          if (data.cell.raw === "") {
            data.cell.styles.lineWidth = 0;
            data.cell.styles.fillColor = [255, 255, 255];
          }
        }
      });

      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.3);
      doc.line(leftMargin, pageHeight - 20, rightMargin, pageHeight - 20);
      doc.setTextColor(148, 163, 184);
      doc.setFontSize(8);
      doc.text("Audit Report - Confidential", leftMargin, pageHeight - 12);
      doc.text(`Ticket #${ticket.id}`, pageWidth / 2, pageHeight - 12, { align: 'center' });
      doc.text(`Page ${pageNumber}`, rightMargin, pageHeight - 12, { align: 'right' });
      pageNumber++;

      // ============ AUDIT TIMELINE PAGE(S) ============
      doc.addPage();
      yPos = 20;

      doc.setFillColor(248, 250, 252);
      doc.rect(leftMargin, yPos - 5, rightMargin - leftMargin, 12, 'F');
      doc.setTextColor(...colors.darkBlue);
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.text(`COMPLETE AUDIT TIMELINE - TICKET #${ticket.id}`, leftMargin + 3, yPos + 3);
      yPos += 12;

      // Filter out Comment Added and Attachment events
      const filteredEvents = ticket.auditEvents.filter(event => 
        event.type !== "Comment Added" && event.type !== "Attachment"
      );

      // Build timeline with SLA information in Status Change and Escalation events
      const timelineData = filteredEvents.map((event, index) => {
        // In the PDF generation sections (both handleExportAllToPDF and exportIndividualToPDF),
// update the actionDetails for Status Change events:

let actionDetails = "";

if (event.type === "Ticket Creation") {
  actionDetails = event.details; // Includes assignment if present
} else if (event.type === "Status Change") {
  let statusChangeDetails = `${event.previous || "N/A"} to ${event.current || "N/A"}`;
  
  const isPendingStatus = (status) => {
    if (!status) return false;
    const statusLower = status.toLowerCase();
    return statusLower.includes("pending at user") || 
           statusLower.includes("pending at external");
  };
  
  let slaIndicator = "";
  if (isPendingStatus(event.current)) {
    slaIndicator = "\n[SLA Paused]";
  }
  if (isPendingStatus(event.previous) && !isPendingStatus(event.current)) {
    slaIndicator = "\n[SLA Resumed]";
  }
  
  // Add reassignment info if present
  if (event.hasReassignment && event.reassignmentDetails) {
    const reassignTo = event.reassignmentDetails.reassignmentUser || 
                      event.reassignmentDetails.newL1User || 
                      event.reassignmentDetails.newL2User || "User";
    slaIndicator += ` | Reassigned to ${reassignTo}`;
  }
  
  actionDetails = statusChangeDetails + slaIndicator;
} else if (event.type === "L1 User Assignment" || event.type === "L2 User Assignment") {
  actionDetails = event.details;
} else if (event.type === "Escalation") {

  let escalationDetails = event.escalationType === "Manual"
    ? `Manually Escalated to ${event.escalatedTo || "N/A"}`
    : `Escalated to ${event.escalatedTo || "N/A"}`;
  
  // Check for SLA Breached
  let isSlaBreached = false;
  
  // First check if this event has escalationReason containing SLA
  if (event.escalationReason && event.escalationReason.toLowerCase().includes("sla")) {
    isSlaBreached = true;
    escalationDetails += ` - ${event.escalationReason}`;
  }
  
  // Check if escalation timestamp matches SLA breach timestamp
  if (ticket.slaMetrics && ticket.slaMetrics.slaBreached && 
      ticket.slaMetrics.slaBreachedAt) {
    const escalationTime = new Date(event.timestamp).getTime();
    const breachTime = new Date(ticket.slaMetrics.slaBreachedAt).getTime();
    const timeDiffMinutes = Math.abs(escalationTime - breachTime) / (1000 * 60);
    
    if (timeDiffMinutes < 5) {
      isSlaBreached = true;
      escalationDetails += ` [SLA Breached - ${ticket.slaMetrics.slaBreachReason || "SLA time exceeded"}]`;
    }
  }
  
  // Also check if this is specifically an SLA breach escalation
  if (event.escalationType && event.escalationType.toLowerCase().includes("sla")) {
    isSlaBreached = true;
    escalationDetails += ` [SLA Breached - ${event.escalationReason || "SLA threshold exceeded"}]`;
  }
  
  // If SLA breached but no message added yet, add generic message
  if (isSlaBreached && !escalationDetails.includes("[SLA Breached]")) {
    escalationDetails += ` [SLA Breached]`;
  }
   // Add reassignment at the end
          if (event.hasReassignment && event.reassignmentDetails) {
            const reassignTo = event.reassignmentDetails.reassignmentUser || 
                              event.reassignmentDetails.newL1User || 
                              event.reassignmentDetails.newL2User || "User";
            escalationDetails += ` | Reassigned to ${reassignTo}`;
          }
          
  
  actionDetails = escalationDetails;
}else if (event.type === "System Update") {
          actionDetails = "System Update";
        }


        
        return [
          (index + 1).toString(),
          event.type === "Ticket Creation"
          ? (event.ticketCreatedDateTime || "N/A")
          : formatTimestampForExport(event.timestamp),
          event.type,
          event.eventType === "L1_ASSIGNMENT"
            ? `${event.user} (HO)`
            : event.eventType === "L2_ASSIGNMENT"
            ? `${event.user} (Support Team Admin)`
            : event.eventType === "BULK_ASSIGNMENT"
            ? `${event.user} (Support Team Admin)`
            : event.eventType === "ESCALATION" && event.escalationType === "Auto"
            ? `System (Auto)`
            : `${event.user} (${event.performedByUserLevel || "N/A"})`,          
          actionDetails
        ];
      });

      doc.autoTable({
        startY: yPos,
        head: [["S.No", "Event Date & Time", "Event Type", "Performed By", "Action"]],
        body: timelineData,
        margin: { left: leftMargin, right: leftMargin },
        styles: {
          fontSize: 6.5,
          cellPadding: 2,
          lineColor: [226, 232, 240],
          lineWidth: 0.15,
          textColor: [30, 41, 59]
        },
        headStyles: {
          fillColor: [71, 85, 105],
          textColor: [255, 255, 255],
          fontSize: 7,
          fontStyle: 'bold'
        },
        alternateRowStyles: {
          fillColor: [249, 250, 251]
        },
        columnStyles: {
          0: { cellWidth: 12, halign: 'center', textColor: [100, 116, 139] },
          1: { cellWidth: 35 },  // Reduced from 30
          2: { cellWidth: 28 },  // Reduced from 28
          3: { cellWidth: 32 },  // Reduced from 25
          4: { cellWidth: 80 },  // Reduced from 20 - User Level

        },
        didDrawCell: (data) => {
          if (data.section === 'body' && data.column.index === 2) {
            const eventType = data.cell.raw;

            const eventColors = {
              "Ticket Creation": colors.green,
              "Status Change": colors.blue,
              "Escalation": colors.purple,
              "System Update": colors.gray
            };

            const color = eventColors[eventType] || colors.gray;
            doc.setTextColor(...color);
          }
        },
        didDrawPage: (data) => {
          // Footer for timeline pages
          doc.setDrawColor(226, 232, 240);
          doc.setLineWidth(0.3);
          doc.line(leftMargin, pageHeight - 20, rightMargin, pageHeight - 20);

          doc.setTextColor(148, 163, 184);
          doc.setFontSize(8);

          doc.text("Audit Report - Confidential", leftMargin, pageHeight - 12);
          doc.text(`Ticket #${ticket.id}`, pageWidth / 2, pageHeight - 12, { align: 'center' });
          doc.text(`Page ${pageNumber}`, rightMargin, pageHeight - 12, { align: 'right' });
        }
      });
      
      pageNumber++;
    });

    // Save PDF
    const fileName = `Bulk_Audit_History_${totalTickets}_Tickets_${formattedDate}.pdf`;
    doc.save(fileName);
    
    toast.success(`Bulk PDF report generated successfully! (${totalTickets} tickets)`);
    
  } catch (error) {
    console.error("Error exporting all to PDF:", error);
    toast.error("Failed to export bulk data to PDF. Please try again.");
  } finally {
    setExporting(false);
  }
};
const exportIndividualToExcel = async (ticketId) => {
  setExporting(true);
  try {
    const auditHistoryEndpoint = getAuditHistory + ticketId;
    const apiResponse = await postDataWithBody(auditHistoryEndpoint, {}, {});
    const ticket = processAuditHistoryData(apiResponse);

    const ageingData = calculateAgeingFromAuditHistory(apiResponse)

    const wb = XLSX.utils.book_new();
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);

    // Helper function to apply bold styling to specific cells
    const applyBoldStyle = (worksheet, cellRefs) => {
      cellRefs.forEach(ref => {
        if (worksheet[ref]) {
          worksheet[ref].s = {
            font: { bold: true }
          };
        }
      });
    };

    const summaryData = [
      ["AUDIT HISTORY REPORT - COMPREHENSIVE ANALYSIS"],
      ["Ticket Tracking & Analysis Report"],
      ["REPORT INFORMATION"],
      ["Ticket ID:", ticket.id],
      ["Subject:", ticket.subject],
      ["Priority:", ticket.priority],
      ["Current Status:", ticket.status],
      ["Category:", ticket.category || "N/A"],
      ["Subcategory:", ticket.subcategory || "N/A"],
    ];

    const ws1 = XLSX.utils.aoa_to_sheet(summaryData);
    ws1['!cols'] = [{ wch: 25 }, { wch: 50 }];
    
    // Apply bold to headings in Sheet 1
    applyBoldStyle(ws1, [
      'A1', 'A2', // Main title and subtitle
      'A4', // REPORT INFORMATION
      'A12', // SLA METRICS
      'A5', 'A6', 'A7', 'A8', 'A9', // Report info labels
      'A13', 'A14', 'A15', 'A16', 'A17' // SLA metrics labels
    ]);
    
    XLSX.utils.book_append_sheet(wb, ws1, "Report Summary");

    const ticketDetailsData = [
      ["TICKET DETAILS"],
      ["Ticket Created Date & Time", ticket.auditEvents[0]?.ticketCreatedDateTime || "N/A"],
      ["Ticket ID", ticket.id],
      ["Parent Ticket ID", ticket.parentGrievanceTicketId],
      ["Subject", ticket.subject],
      ["Current Status", ticket.status],
      ["Priority", ticket.priority],
      ["Category", ticket.category || "N/A"],
      ["Subcategory", ticket.subcategory || "N/A"],
      ["Bank Name", ticket.bank],
      ["Branch Name", ticket.branch],
      ["Support Team", ticket.supportTeam || "N/A"],
      ["Escalation Level", ticket.escalationLevel || "N/A"],
      ["Resolution Date & Time", ticket.resolutionDateTime || "N/A"],
      ["Resolved Ageing", ageingData.resolvedAgeing],  // NEW
      ["Closed Ageing", ageingData.closedAgeing], 
    ];

    const ws2 = XLSX.utils.aoa_to_sheet(ticketDetailsData);
    ws2['!cols'] = [{ wch: 30 }, { wch: 40 }];
    
    // Apply bold to headings in Sheet 2
    applyBoldStyle(ws2, [
      'A1', 'A2', // Main title
      'A4', 'B4', // Column headers
      'A5', 'A20' // Section headers
    ]);
    
    XLSX.utils.book_append_sheet(wb, ws2, "Ticket Details");

    // =============== SHEET 3: COMPLETE AUDIT TIMELINE (NO COMMENTS, NO ATTACHMENTS, NO AGING) ===============
    const timelineData = [
      ["COMPLETE AUDIT TIMELINE"],
      [`Ticket #${ticket.id}`],
      [""],
      ["S.No", "Event Date & Time", "Event Type", "Performed By", "Action"]
    ];

    // Filter out Comment Added and Attachment events
    const filteredEvents = ticket.auditEvents.filter(event => 
      event.type !== "Comment Added" && event.type !== "Attachment"
    );

    filteredEvents.forEach((event, index) => {
      // In the Excel generation sections (both handleExportAllToExcel and exportIndividualToExcel),
// update the actionDetails for Status Change events:

let actionDetails = "";

switch (event.type) {
  case "Ticket Creation":
    actionDetails = event.details; // This now includes assignment if present
    break;
    
  case "Status Change":
    let statusChangeDetails = `${event.previous || "N/A"} to ${event.current || "N/A"}`;
    
    // Helper function for pending status check
    const isPendingStatus = (status) => {
      if (!status) return false;
      const statusLower = status.toLowerCase();
      return statusLower.includes("issue pending at user level") || 
             statusLower.includes("issue pending at user") ||
             statusLower.includes("issue pending at external vendor") ||
             statusLower.includes("issue pending at external") ||
             statusLower.includes("pending at user") ||
             statusLower.includes("pending at external");
    };
    
    // Check for SLA Paused
    if (isPendingStatus(event.current)) {
      statusChangeDetails += " [SLA Paused]";
    }
    
    // Check for SLA Resumed
    if (isPendingStatus(event.previous) && !isPendingStatus(event.current)) {
      statusChangeDetails += " [SLA Resumed]";
    }
    
    // Add reassignment info if present
    if (event.hasReassignment && event.reassignmentDetails) {
      const reassignTo = event.reassignmentDetails.reassignmentUser || 
                        event.reassignmentDetails.newL1User || 
                        event.reassignmentDetails.newL2User || "User";
      statusChangeDetails += ` | Reassigned to ${reassignTo}`;  // Changed → to |
    }
    
    actionDetails = statusChangeDetails;
    break;
    
  case "Escalation":
    let escalationDetails = event.escalationType === "Manual"
      ? `Manually Escalated to ${event.escalatedTo || "N/A"}`
      : `Escalated to ${event.escalatedTo || "N/A"}`;
    
    // SLA Breached check
    let isSlaBreached = false;
    if (event.escalationReason && event.escalationReason.toLowerCase().includes("sla")) {
      isSlaBreached = true;
      escalationDetails += ` - ${event.escalationReason}`;
    }
    if (ticket.slaMetrics && ticket.slaMetrics.slaBreached && ticket.slaMetrics.slaBreachedAt) {
      const escalationTime = new Date(event.timestamp).getTime();
      const breachTime = new Date(ticket.slaMetrics.slaBreachedAt).getTime();
      const timeDiffMinutes = Math.abs(escalationTime - breachTime) / (1000 * 60);
      if (timeDiffMinutes < 5) {
        isSlaBreached = true;
        escalationDetails += ` [SLA Breached - ${ticket.slaMetrics.slaBreachReason || "SLA time exceeded"}]`;
      }
    }
    if (event.escalationType && event.escalationType.toLowerCase().includes("sla")) {
      isSlaBreached = true;
      escalationDetails += ` [SLA Breached - ${event.escalationReason || "SLA threshold exceeded"}]`;
    }
    if (isSlaBreached && !escalationDetails.includes("[SLA Breached]")) {
      escalationDetails += ` [SLA Breached]`;
    }
    
    // Add reassignment info if present
    if (event.hasReassignment && event.reassignmentDetails) {
      const reassignTo = event.reassignmentDetails.reassignmentUser || 
                        event.reassignmentDetails.newL1User || 
                        event.reassignmentDetails.newL2User || "User";
      escalationDetails += ` | Reassigned to ${reassignTo}`;  // Changed → to |
    }
    
    actionDetails = escalationDetails;
    break;
    
  case "Support Team Admin Bulk Assign":
  case "Bulk Assignment":
  case "L1 User Reassignment":
  case "L1 User Assignment":
  case "L2 User Reassignment":
  case "L2 User Assignment":
    actionDetails = event.details;
    break;
    
  case "System Update":
    actionDetails = "System Update";
    break;
    
  default:
    actionDetails = event.type;
}

      timelineData.push([
        index + 1,
        event.type === "Ticket Creation"
          ? (event.ticketCreatedDateTime || "N/A")
          : formatTimestampForExport(event.timestamp),
        event.type,
        event.eventType === "L1_ASSIGNMENT"
            ? `${event.user} (HO)`
            : event.eventType === "L2_ASSIGNMENT"
            ? `${event.user} (Support Team Admin)`
            : event.eventType === "BULK_ASSIGNMENT"
            ? `${event.user} (Support Team Admin)`
            : event.eventType === "ESCALATION" && event.escalationType === "Auto"
            ? `System (Auto)`
            : `${event.user} (${event.performedByUserLevel || "N/A"})`,
        actionDetails,
      ]);
    });

    const ws3 = XLSX.utils.aoa_to_sheet(timelineData);
    ws3['!cols'] = [
      { wch: 5 },
      { wch: 20 },
      { wch: 18 },
      { wch: 18 },
      { wch: 45 },
    ];
    
    // Apply bold to headings in Sheet 3
    applyBoldStyle(ws3, [
      'A1', 'A2', // Main title
      'A4', 'B4', 'C4', 'D4', 'E4', // Column headers
    ]);
    
    XLSX.utils.book_append_sheet(wb, ws3, "Audit Timeline");

   

    // Save the Excel file
    const fileName = `Audit_Timeline_Ticket_${ticket.id}_${timestamp}.xlsx`;
    XLSX.writeFile(wb, fileName);

    toast.success(`Audit timeline report generated for Ticket #${ticket.id}`);
  } catch (error) {
    console.error("Error exporting to Excel:", error);
    toast.error("Failed to export to Excel. Please try again.");
  } finally {
    setExporting(false);
  }
};

const exportIndividualToPDF = async (ticketId) => {
  setExporting(true);
  try {
    const auditHistoryEndpoint = getAuditHistory + ticketId;
    const apiResponse = await postDataWithBody(auditHistoryEndpoint, {}, {});
    const ticket = processAuditHistoryData(apiResponse);

     const ageingData = calculateAgeingFromAuditHistory(apiResponse); // or apiResponse for individual

    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    doc.setFont("helvetica");

    const colors = {
      darkBlue: [0, 51, 102],
      blue: [59, 130, 246],
      lightBlue: [219, 234, 254],
      green: [16, 185, 129],
      lightGreen: [220, 252, 231],
      orange: [251, 146, 60],
      lightOrange: [255, 237, 213],
      red: [239, 68, 68],
      lightRed: [254, 226, 226],
      gray: [107, 114, 128],
      lightGray: [243, 244, 246],
      purple: [139, 92, 246],
      lightPurple: [237, 233, 254],
      pink: [236, 72, 153],
      lightPink: [252, 231, 243],
      teal: [20, 184, 166] // Added teal color for Reassignment
    };

    // Helper function to set text color from RGB array
    const setTextColor = (colorArray) => {
      if (colorArray && colorArray.length === 3) {
        doc.setTextColor(colorArray[0], colorArray[1], colorArray[2]);
      }
    };

    // Helper function to set fill color from RGB array
    const setFillColor = (colorArray) => {
      if (colorArray && colorArray.length === 3) {
        doc.setFillColor(colorArray[0], colorArray[1], colorArray[2]);
      }
    };

    // Helper function to set draw color from RGB array
    const setDrawColor = (colorArray) => {
      if (colorArray && colorArray.length === 3) {
        doc.setDrawColor(colorArray[0], colorArray[1], colorArray[2]);
      }
    };

    const leftMargin = 15;
    const rightMargin = 195;
    const pageWidth = 210;
    const pageHeight = 297;

    // ============ PAGE 1: IMPROVED SUMMARY PAGE WITH SLA METRICS ============
    // Top Header Bar
    setFillColor(colors.darkBlue);
    doc.rect(0, 0, pageWidth, 45, 'F');

    // White Title
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.setFont(undefined, 'bold');
    doc.text("AUDIT HISTORY REPORT", pageWidth / 2, 22, { align: 'center' });
    
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text("Ticket Tracking & Analysis", pageWidth / 2, 32, { align: 'center' });

    let yPos = 55;

    // Report Info Box
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(leftMargin, yPos, rightMargin - leftMargin, 25, 2, 2, 'F');
    
    doc.setTextColor(71, 85, 105);
    doc.setFontSize(8.5);
    doc.setFont(undefined, 'normal');
    
    const currentDate = new Date();
    const formattedDate = `${currentDate.getDate().toString().padStart(2, '0')}-${(currentDate.getMonth() + 1).toString().padStart(2, '0')}-${currentDate.getFullYear()}`;
    
    doc.text(`Report Generated: ${formattedDate}`, leftMargin + 5, yPos + 8);
    doc.text(`Ticket #${ticket.id}`, leftMargin + 5, yPos + 14);
    doc.text(`Created On: ${formatTimestampForExport(ticket.createdOn).split(' ')[0]}`, rightMargin - 50, yPos + 14);

    yPos += 32;

    // ===== KEY METRICS OVERVIEW WITH SLA METRICS =====
setTextColor(colors.darkBlue);
doc.setFontSize(11);
doc.setFont(undefined, 'bold');
doc.text("KEY METRICS OVERVIEW", leftMargin, yPos);
yPos += 8;

// Calculate comprehensive metrics (filtered - no comments, no attachments)
const filteredEvents = ticket.auditEvents.filter(event => 
  event.type !== "Comment Added" && event.type !== "Attachment"
);

const statusChangesCount = filteredEvents.filter(e => e.type === "Status Change").length;
const escalationsCount = filteredEvents.filter(e => e.type === "Escalation").length;
// eslint-disable-next-line no-unused-vars
const slaPausedCount = ticket.slaMetrics.slaPausedCount || 0;
// eslint-disable-next-line no-unused-vars
const slaBreached = ticket.slaMetrics.slaBreached ? "Yes" : "No";

const metricBoxes = [
  // { 
  //   label: "Total Events", 
  //   value: filteredEvents.length.toString(), 
  //   color: colors.blue, 
  //   bgColor: colors.lightBlue 
  // },
  { 
    label: "Status Changes", 
    value: statusChangesCount.toString(), 
    color: colors.purple, 
    bgColor: colors.lightPurple 
  },
  { 
    label: "Escalations", 
    value: escalationsCount.toString(), 
    color: colors.pink, 
    bgColor: colors.lightPink 
  },
  { 
    label: "SLA Paused", 
    value: ticket.slaMetrics?.slaPausedCount?.toString() || "0", 
    color: colors.orange, 
    bgColor: colors.lightOrange 
  },
];

const boxWidth = 58;
const boxHeight = 28;
const boxGap = 3;

metricBoxes.forEach((box, index) => {
  const col = index % 3;
  const row = Math.floor(index / 3);
  const x = leftMargin + (col * (boxWidth + boxGap));
  const y = yPos + (row * (boxHeight + 4));

  // Box background with border
  setFillColor(box.bgColor);
  setDrawColor(box.color);
  doc.setLineWidth(0.5);
  doc.roundedRect(x, y, boxWidth, boxHeight, 3, 3, 'FD');

  // Value
  setTextColor(box.color);
  doc.setFontSize(18);
  doc.setFont(undefined, 'bold');
  doc.text(box.value, x + boxWidth / 2, y + 14, { align: 'center' });

  // Label
  doc.setTextColor(71, 85, 105);
  doc.setFontSize(8);
  doc.setFont(undefined, 'normal');
  doc.text(box.label, x + boxWidth / 2, y + 22, { align: 'center' });
});

// Calculate the bottom position of the last row of metrics
const lastRowIndex = Math.floor((metricBoxes.length - 1) / 3);
const lastRowY = yPos + (lastRowIndex * (boxHeight + 4)) + boxHeight;

// Add 12mm gap after the metrics boxes
yPos = lastRowY + 12;

// ===== ACTIVITY SUMMARY =====
setTextColor(colors.darkBlue);
doc.setFontSize(11);
doc.setFont(undefined, 'bold');
doc.text("ACTIVITY SUMMARY", leftMargin, yPos);
yPos += 8;

    doc.setFillColor(248, 250, 252);
    doc.roundedRect(leftMargin, yPos, rightMargin - leftMargin, 35, 2, 2, 'F');
    
    yPos += 8;

    // Get first and last activity dates from filtered events
    // const firstActivity = filteredEvents.length > 0 ? formatTimestampForExport(filteredEvents[0]?.timestamp) : "N/A";
    // const lastActivity = filteredEvents.length > 0 ? formatTimestampForExport(filteredEvents[filteredEvents.length - 1]?.timestamp) : "N/A";

    doc.setFontSize(8);
    doc.setTextColor(71, 85, 105);
    doc.setFont(undefined, 'normal');
    
    const activityCol1X = leftMargin + 5;
    const activityCol2X = leftMargin + 95;
    
   
    
    
    // Priority
    doc.setFont(undefined, 'normal');
    doc.setTextColor(71, 85, 105);
    doc.text("Priority:", activityCol1X, yPos);
    doc.setFont(undefined, 'bold');
    const priorityColor = ticket.priority === "High" ? colors.red : ticket.priority === "Medium" ? colors.orange : colors.gray;
    setTextColor(priorityColor);
    doc.text(ticket.priority, activityCol1X + 25, yPos);
    
    // Current Status
    doc.setFont(undefined, 'normal');
    doc.setTextColor(71, 85, 105);
    doc.text("Current Status:", activityCol2X, yPos);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(30, 41, 59);
    doc.text(ticket.status, activityCol2X + 25, yPos);
    
    yPos += 6;
    
    // Category
    doc.setFont(undefined, 'normal');
    doc.setTextColor(71, 85, 105);
    doc.text("Category:", activityCol1X, yPos);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(30, 41, 59);
    const categoryText = ticket.category || "N/A";
    if (doc.getTextWidth(categoryText) > 60) {
      doc.setFontSize(7);
      doc.text(categoryText.substring(0, 40) + "...", activityCol1X + 25, yPos);
      doc.setFontSize(8);
    } else {
      doc.text(categoryText, activityCol1X + 25, yPos);
    }
    
    // Subcategory
    doc.setFont(undefined, 'normal');
    doc.setTextColor(71, 85, 105);
    doc.text("Subcategory:", activityCol2X, yPos);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(30, 41, 59);
    const subcategoryText = ticket.subcategory || "N/A";
    if (doc.getTextWidth(subcategoryText) > 50) {
      doc.setFontSize(7);
      doc.text(subcategoryText.substring(0, 35) + "...", activityCol2X + 25, yPos);
      doc.setFontSize(8);
    } else {
      doc.text(subcategoryText, activityCol2X + 25, yPos);
    }

    // ===== FOOTER PAGE 1 =====
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.3);
    doc.line(leftMargin, pageHeight - 20, rightMargin, pageHeight - 20);
    
    doc.setTextColor(148, 163, 184);
    doc.setFontSize(8);
    doc.setFont(undefined, 'normal');
    doc.text("Audit Report - Confidential", leftMargin, pageHeight - 12);
    doc.text(`Generated: ${formattedDate}`, pageWidth / 2, pageHeight - 12, { align: 'center' });
    doc.text("Page 1", rightMargin, pageHeight - 12, { align: 'right' });

    // ============ PAGE 2: TICKET DETAILS WITH SLA INFO ============
    doc.addPage();
    let pageNumber = 2;
    yPos = 20;

    // Page Header
    setFillColor(colors.darkBlue);
    doc.rect(0, 0, pageWidth, 35, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text("TICKET DETAILS", leftMargin, 18);
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(`Ticket #${ticket.id}`, leftMargin, 28);

    yPos = 50;

    // ========== TICKET INFORMATION WITH SLA METRICS ==========
    doc.autoTable({
      startY: yPos,
      head: [["Field", "Value"]],
      body: [
        ["Ticket ID", ticket.id],
        ["Parent Ticket ID", ticket.parentGrievanceTicketId],
        ["Ticket Created Date & Time", ticket.auditEvents[0]?.ticketCreatedDateTime || "N/A"],
        ["Subject", ticket.subject],
        [""],
        ["Current Status", ticket.status],
        ["Priority", ticket.priority],
        ["Category", ticket.category || "N/A"],
        ["Subcategory", ticket.subcategory || "N/A"],
        [""],
        ["Bank Name", ticket.bank],
        ["Branch Name", ticket.branch],
        [""],
        ["Support Team", ticket.supportTeam || "N/A"],
        ["Escalation Level", ticket.escalationLevel || "N/A"],
        ["Resolution Date & Time", ticket.resolutionDateTime || "N/A"], // NEW LINE
        ["Resolved Ageing", ageingData.resolvedAgeing],  // NEW
      ["Closed Ageing", ageingData.closedAgeing],      // NEW
      ],
      margin: { left: leftMargin, right: leftMargin },
      styles: {
        fontSize: 8.5,
        cellPadding: 2.5,
        lineColor: [226, 232, 240],
        lineWidth: 0.2,
        textColor: [30, 41, 59]
      },
      headStyles: {
        fillColor: [colors.darkBlue[0], colors.darkBlue[1], colors.darkBlue[2]],
        textColor: [255, 255, 255],
        fontSize: 9,
        fontStyle: 'bold'
      },
      columnStyles: {
        0: { cellWidth: 50, fontStyle: 'bold', fillColor: [249, 250, 251], textColor: [colors.gray[0], colors.gray[1], colors.gray[2]] },
        1: { cellWidth: 130 }
      },
      didParseCell: function(data) {
        if (data.cell.raw === "") {
          data.cell.styles.lineWidth = 0;
          data.cell.styles.fillColor = [255, 255, 255];
        }
        // Highlight SLA Breached row
        if (data.cell.raw === "SLA Breached" && data.section === 'body') {
          if (data.row.index === 24 && data.cell.raw === "Yes") {
            data.cell.styles.textColor = [colors.red[0], colors.red[1], colors.red[2]];
            data.cell.styles.fontStyle = 'bold';
          }
        }
      }
    });

    // Page Footer
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.3);
    doc.line(leftMargin, pageHeight - 20, rightMargin, pageHeight - 20);
    doc.setTextColor(148, 163, 184);
    doc.setFontSize(8);
    doc.text("Audit Report - Confidential", leftMargin, pageHeight - 12);
    doc.text(`Ticket #${ticket.id}`, pageWidth / 2, pageHeight - 12, { align: 'center' });
    doc.text(`Page ${pageNumber}`, rightMargin, pageHeight - 12, { align: 'right' });

    // ============ PAGE 3+: AUDIT TIMELINE (NO COMMENTS, NO ATTACHMENTS, NO AGING) ============
    doc.addPage();
    pageNumber = 3;
    yPos = 20;

    // Page Header
    doc.setFillColor(248, 250, 252);
    doc.rect(leftMargin, yPos - 5, rightMargin - leftMargin, 12, 'F');
    setTextColor(colors.darkBlue);
    doc.setFontSize(11);
    doc.setFont(undefined, 'bold');
    doc.text(`COMPLETE AUDIT TIMELINE - TICKET #${ticket.id}`, leftMargin + 3, yPos + 3);
    yPos += 12;

    // Filter out Comment Added and Attachment events
    const filteredTimelineEvents = ticket.auditEvents.filter(event => 
      event.type !== "Comment Added" && event.type !== "Attachment"
    );

    // Build timeline data WITHOUT Aging column
    const timelineData = filteredTimelineEvents.map((event, index) => {
      // In the PDF generation sections (both handleExportAllToPDF and exportIndividualToPDF),
// update the actionDetails for Status Change events:

let actionDetails = "";

      if (event.type === "Ticket Creation") {
        actionDetails = event.details; // Already includes assignment if present
      } else if (event.type === "Status Change") {
        let statusChangeDetails = `${event.previous || "N/A"} to ${event.current || "N/A"}`;
        
        const isPendingStatus = (status) => {
          if (!status) return false;
          const statusLower = status.toLowerCase();
          return statusLower.includes("pending at user") || 
                 statusLower.includes("pending at external");
        };
        
        let slaIndicator = "";
        if (isPendingStatus(event.current)) {
          slaIndicator = "\n[SLA Paused]";
        }
        if (isPendingStatus(event.previous) && !isPendingStatus(event.current)) {
          slaIndicator = "\n[SLA Resumed]";
        }
        
        // Add reassignment info if present
        if (event.hasReassignment && event.reassignmentDetails) {
          const reassignTo = event.reassignmentDetails.reassignmentUser || 
                            event.reassignmentDetails.newL1User || 
                            event.reassignmentDetails.newL2User || "User";
          slaIndicator += ` | Reassigned to ${reassignTo}`;
        }
        
        actionDetails = statusChangeDetails + slaIndicator;
      } else if (event.type === "Escalation") {
        let escalationDetails = event.escalationType === "Manual"
          ? `Manually Escalated to ${event.escalatedTo || "N/A"}`
          : `Escalated to ${event.escalatedTo || "N/A"}`;
        
        // Check for SLA Breached
        let isSlaBreached = false;
        
        if (event.escalationReason && event.escalationReason.toLowerCase().includes("sla")) {
          isSlaBreached = true;
          escalationDetails += ` - ${event.escalationReason}`;
        }
        
        if (ticket.slaMetrics && ticket.slaMetrics.slaBreached && 
            ticket.slaMetrics.slaBreachedAt) {
          const escalationTime = new Date(event.timestamp).getTime();
          const breachTime = new Date(ticket.slaMetrics.slaBreachedAt).getTime();
          const timeDiffMinutes = Math.abs(escalationTime - breachTime) / (1000 * 60);
          
          if (timeDiffMinutes < 5) {
            isSlaBreached = true;
            escalationDetails += ` [SLA Breached - ${ticket.slaMetrics.slaBreachReason || "SLA time exceeded"}]`;
          }
        }
        
        if (event.escalationType && event.escalationType.toLowerCase().includes("sla")) {
          isSlaBreached = true;
          escalationDetails += ` [SLA Breached - ${event.escalationReason || "SLA threshold exceeded"}]`;
        }
        
        if (isSlaBreached && !escalationDetails.includes("[SLA Breached]")) {
          escalationDetails += ` [SLA Breached]`;
        }
        
        // Add reassignment at the end
        if (event.hasReassignment && event.reassignmentDetails) {
          const reassignTo = event.reassignmentDetails.reassignmentUser || 
                            event.reassignmentDetails.newL1User || 
                            event.reassignmentDetails.newL2User || "User";
          escalationDetails += ` | Reassigned to ${reassignTo}`;
        }
        
        actionDetails = escalationDetails;
      } else if (event.type === "System Update") {
        actionDetails = "System Update";
      } else {
        actionDetails = event.details || event.type;
      }

      return [
        (index + 1).toString(),
        event.type === "Ticket Creation"
          ? (event.ticketCreatedDateTime || "N/A")
          : formatTimestampForExport(event.timestamp),
        event.type,
        event.eventType === "L1_ASSIGNMENT"
            ? `${event.user} (HO)`
            : event.eventType === "L2_ASSIGNMENT"
            ? `${event.user} (Support Team Admin)`
            : event.eventType === "BULK_ASSIGNMENT"
            ? `${event.user} (Support Team Admin)`
            : event.eventType === "ESCALATION" && event.escalationType === "Auto"
            ? `System (Auto)`
            : `${event.user} (${event.performedByUserLevel || "N/A"})`,
        actionDetails
      ];
    });

    doc.autoTable({
      startY: yPos,
      head: [["S.No", "Event Date & Time", "Event Type", "Performed By", "Action"]],
      body: timelineData,
      margin: { left: leftMargin, right: leftMargin },
      styles: {
        fontSize: 6.5,
        cellPadding: 2,
        lineColor: [226, 232, 240],
        lineWidth: 0.15,
        textColor: [30, 41, 59]
      },
      headStyles: {
        fillColor: [71, 85, 105],
        textColor: [255, 255, 255],
        fontSize: 7,
        fontStyle: 'bold'
      },
      alternateRowStyles: {
        fillColor: [249, 250, 251]
      },
      columnStyles: {
        0: { cellWidth: 12, halign: 'center', textColor: [100, 116, 139] },
        1: { cellWidth: 35 }, // Reduced from 35
        2: { cellWidth: 28 }, // Reduced from 32
        3: { cellWidth: 32 }, // Reduced from 30
        4: { cellWidth: 80 }, // NEW - User Level
      },
      didDrawCell: (data) => {
        if (data.section === 'body' && data.column.index === 2) {
          const eventType = data.cell.raw;

          const eventColors = {
            "Ticket Creation": colors.green,
            "Status Change": colors.blue,
            "Escalation": colors.purple,
            "Reassignment": colors.teal,
            "System Update": colors.gray
          };

          const color = eventColors[eventType] || colors.gray;
          if (color) {
            doc.setTextColor(color[0], color[1], color[2]);
          }
        }
        
        // Highlight SLA Paused and SLA Breached in Action column
        if (data.section === 'body' && data.column.index === 4) {
          const actionText = data.cell.raw;
          if (actionText && actionText.includes("[SLA Paused]")) {
            doc.setTextColor(colors.orange[0], colors.orange[1], colors.orange[2]);
          } else if (actionText && actionText.includes("[SLA Breached]")) {
            doc.setTextColor(colors.red[0], colors.red[1], colors.red[2]);
          }
        }
      },
      didDrawPage: (data) => {
        doc.setDrawColor(226, 232, 240);
        doc.setLineWidth(0.3);
        doc.line(leftMargin, pageHeight - 20, rightMargin, pageHeight - 20);

        doc.setTextColor(148, 163, 184);
        doc.setFontSize(8);

        doc.text("Audit Report - Confidential", leftMargin, pageHeight - 12);
        doc.text(`Ticket #${ticket.id}`, pageWidth / 2, pageHeight - 12, { align: 'center' });
        doc.text(`Page ${pageNumber}`, rightMargin, pageHeight - 12, { align: 'right' });

        pageNumber++;
      }
    });

    // Save PDF
    const fileName = `Audit_History_Ticket_${ticket.id}_${new Date().toISOString().slice(0,10)}.pdf`;
    doc.save(fileName);
    
    toast.success(`PDF report generated for Ticket #${ticket.id}`);
    
  } catch (error) {
    console.error("Error exporting to PDF:", error);
    toast.error("Failed to export to PDF. Please try again.");
  } finally {
    setExporting(false);
  }
};
  // Fetch user profile
  const fetchUserProfile = async () => {
    try {
      const response = await getData(getThemeURL);
      if (response && Array.isArray(response) && response.length > 0) {
        setUserId(response[0].id);
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
    }
  };

  // Fetch grievance data
  const getAllGrievanceData = async () => {
    setLoader(true);
    try {
      const userProfile = await getData(getThemeURL);
      if (!userProfile || !Array.isArray(userProfile) || userProfile.length === 0) {
        toast.error("Failed to fetch user profile");
        setLoader(false);
        return;
      }

      const user = userProfile[0];
      const payload = createGrievancePayload(user);
      const grievanceData = await postDataWithBody(fetchGrievanceData, payload, {});
      
      if (Array.isArray(grievanceData)) {
        const processedData = processGrievanceData(grievanceData);
        setComplaints(processedData);
        setFilteredComplaints(processedData);
        setIsLoaded(true);
      }
    } catch (error) {
      console.error("Error fetching grievance data:", error);
      toast.error("Failed to load grievance data");
      setIsLoaded(true);
    } finally {
      setLoader(false);
    }
  };

  const createGrievancePayload = (userProfile) => {
    const { role_name, bank_code, id: userId, is_admin, user_level } = userProfile;

    if (role_name === "SUPER ADMIN") {
      return {
        admin_role: "SA",
        bank_code: null,
        branch_code: null,
        userId: userId || null,
        userLevel: user_level || null,
      };
    }

    if (is_admin === true) {
      return {
        admin_role: "HO",
        bank_code: bank_code || null,
        branch_code: null,
        userId: userId || null,
        userLevel: user_level || null,
      };
    }

    return {
      admin_role: "USER",
      bank_code: null,
      branch_code: null,
      userId: userId || null,
      userLevel: user_level || null,
    };
  };

  const processGrievanceData = (grievanceData) => {
    return grievanceData.map((item) => {
      const auditCount = calculateAuditCount(item);
      const hasOldGrievance = item.oldGrievanceInformationEntity && 
                              Array.isArray(item.oldGrievanceInformationEntity) && 
                              item.oldGrievanceInformationEntity.length > 0;
    // ✅ ADD THIS LINE:
    const isReopened = item.oldGrievanceId !== null && item.oldGrievanceId !== undefined;
      
      return {
        id: item.grievanceSeqId,
        ticketIdInternal: item.id,
        bank: item.bankNameShort,
        branch: item.branchNameShort,
        date: formatDateTime(item.ticketDateAndTime || item.created_on),
        status: getStatusFromId(item.status),
        priority: item.priority,
        requesterBy: item.requesterBy,
        handler: item.assignedTeamName,
        assignToUserName: item.assignToUserName || "Unassigned",
        originalData: item,
        auditCount: auditCount,
        hasOldGrievance: hasOldGrievance,
        oldGrievanceData: hasOldGrievance ? item.oldGrievanceInformationEntity : null,
        isReopened: isReopened,  // ADD THIS LINE
        oldGrievanceId: item.oldGrievanceId || null  // ADD THIS LINE
      };
    });
  };

  const calculateAuditCount = (grievance) => {
    let count = 1;
    
    if (grievance.grievanceStatusMapEntities) {
      count += grievance.grievanceStatusMapEntities.length;
    }
    
    if (grievance.commentsMap) {
      count += grievance.commentsMap.length;
    }
    
    if (grievance.mediaList) {
      count += grievance.mediaList.length;
    }
    
    if (grievance.priorityHistory) count += grievance.priorityHistory.length;
    if (grievance.departmentHistory) count += grievance.departmentHistory.length;
    if (grievance.assignmentHistory) count += grievance.assignmentHistory.length - 1;
    if (grievance.slaChanges) count += grievance.slaChanges.length;
    if (grievance.reopenHistory) count += grievance.reopenHistory.length;
    if (grievance.closureDate) count += 1;
    
    return count;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    
    let date;
    try {
      date = new Date(dateString);
      if (isNaN(date.getTime())) {
        date = new Date(dateString.replace(/\s+/g, 'T').replace(/\./g, '-'));
      }
    } catch (error) {
      return "Invalid Date";
    }
    
    if (isNaN(date)) return "N/A";

    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = date.getFullYear();
    
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    const formattedHours = String(hours).padStart(2, "0");

    return `${dd}-${mm}-${yyyy} ${formattedHours}:${minutes} ${ampm}`;
  };

  const getStatusFromId = (statusId) => {
    const statusMap = {
      837: "Open",
      838: "In Progress",
      839: "Hold",
      840: "Resolved",
      841: "Closed",
      842: "Rejected",
      845: "Escalated to L2",
      846: "Accept",
      850: "Issue Pending at User Level",
      851: "Issue Pending at External Vendor",
    };
    return statusMap[statusId] || "Open";
  };

  const applyFilters = () => {
    let filtered = [...complaints];

    // Apply search filter
    if (searchQuery.trim() !== "") {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((parentItem) => {
        // Check if parent matches
        const parentMatches = (
          parentItem.id.toString().toLowerCase().includes(query) ||
          parentItem.bank.toLowerCase().includes(query) ||
          parentItem.branch.toLowerCase().includes(query) ||
          parentItem.requesterBy.toLowerCase().includes(query) ||
          parentItem.handler.toLowerCase().includes(query)
        );

        // Check if child matches (old grievance data)
        let childMatches = false;
        if (parentItem.oldGrievanceData && Array.isArray(parentItem.oldGrievanceData)) {
          childMatches = parentItem.oldGrievanceData.some(child =>
            child.grievanceSeqId?.toString().toLowerCase().includes(query) ||
            child.bankNameShort?.toLowerCase().includes(query) ||
            child.branchNameShort?.toLowerCase().includes(query) ||
            child.requesterBy?.toLowerCase().includes(query) ||
            child.assignedTeamName?.toLowerCase().includes(query)
          );
        }

        // Keep parent if either parent or child matches
        return parentMatches || childMatches;
      });
    }

    // Apply status filter
    if (selectedStatus !== "All Status") {
      if (selectedStatus === "Reopen") {
        // Filter for reopened tickets
        filtered = filtered.filter((item) => item.isReopened === true);
      } else {
        // Filter by regular status
        filtered = filtered.filter((item) => item.status === selectedStatus);
      }
    }

    // Apply date range filter
    if (dateRange.from || dateRange.to) {
      filtered = filtered.filter((item) => {
        const ticketDate = new Date(item.originalData?.ticketDateAndTime || item.originalData?.created_on);

        if (dateRange.from) {
          const fromDate = new Date(dateRange.from);
          fromDate.setHours(0, 0, 0, 0);
          if (ticketDate < fromDate) return false;
        }

        if (dateRange.to) {
          const toDate = new Date(dateRange.to);
          toDate.setHours(23, 59, 59, 999);
          if (ticketDate > toDate) return false;
        }

        return true;
      });
    }

    // Update state
    setFilteredComplaints(filtered);
    setCurrentPage(1);
  };

  const getStatusStyle = (status) => {
    const styles = {
      "Open": { bg: "#FEF3C7", color: "#92400E", icon: "●" },
      "In Progress": { bg: "#DBEAFE", color: "#1E40AF", icon: "◐" },
      "Hold": { bg: "#FEE2E2", color: "#991B1B", icon: "⏸" },
      "Resolved": { bg: "#D1FAE5", color: "#065F46", icon: "✓" },
      "Closed": { bg: "#E0E7FF", color: "#3730A3", icon: "✓" },
      "Rejected": { bg: "#FEE2E2", color: "#991B1B", icon: "✕" },
      "Escalated to L2": { bg: "#FCE7F3", color: "#831843", icon: "↑" },
    };
    return styles[status] || { bg: "#F3F4F6", color: "#374151", icon: "○" };
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleViewDetails = async (ticket) => {
    setLoader(true);
    try {
      const ticketId = ticket.ticketIdInternal;
      const auditHistoryEndpoint = getAuditHistory + ticketId;
      // const auditHistoryResponse = await getData(auditHistoryEndpoint);
      const auditHistoryResponse = await postDataWithBody(auditHistoryEndpoint, {}, {});
      navigate('/grievance/details-audit-history', { 
        state: { 
          auditHistoryData: auditHistoryResponse,
        } 
      });
    } catch (error) {
      console.error("Error fetching audit history:", error);
      toast.error("Failed to load audit history details");
    } finally {
      setLoader(false);
    }
  };

  const clearFilters = () => {
    setSearchQuery("");
    setDateRange({ from: "", to: "" });
    setSelectedStatus("All Status");
    setDateError("");
    setDateInputKey(prev => prev + 1);
  };

  // Render ticket card with expandable functionality
  const renderTicketCard = (ticket, isChild = false) => {
    const isExpanded = expandedTickets.has(ticket.id);
    const statusStyle = getStatusStyle(ticket.status);
    
    return (
      <div 
        key={ticket.id} 
        style={{ 
          marginBottom: isChild ? "0" : "6px",
          backgroundColor: "white",
          borderRadius: isChild ? "0" : "6px",
          border: isChild ? "none" : "1px solid #E2E8F0",
          borderTop: isChild ? "1px solid #E2E8F0" : "1px solid #E2E8F0",
          overflow: "hidden",
          transition: "all 0.2s ease"
        }}
      >
        {/* Ticket Card */}
        <div style={{
          padding: "6px 10px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          transition: "background-color 0.2s ease",
          backgroundColor: isChild ? "#F8FAFC" : "white"
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = isChild ? "#F1F5F9" : "#F8FAFC";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = isChild ? "#F8FAFC" : "white";
        }}
        >
          {/* Left Section */}
          <div style={{ display: "flex", alignItems: "center", gap: "16px", flex: 1 }}>
            {/* Expand/Collapse Button */}
            {!isChild && (
              <div style={{ 
                width: "32px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0
              }}>
                {ticket.hasOldGrievance ? (
                  <button
                    onClick={() => toggleTicketExpansion(ticket.id)}
                    style={{
                      width: "28px",
                      height: "28px",
                      borderRadius: "4px",
                      backgroundColor: "white",
                      border: "2px solid #10B981",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "all 0.2s ease",
                      color: "#10B981",
                      fontSize: "20px",
                      fontWeight: "600",
                      padding: 0
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#10B981";
                      e.currentTarget.style.color = "white";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "white";
                      e.currentTarget.style.color = "#10B981";
                    }}
                  >
                    {isExpanded ? "−" : "+"}
                  </button>
                ) : (
                  <div style={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    backgroundColor: "#CBD5E1"
                  }} />
                )}
              </div>
            )}

            {/* Indentation for child tickets */}
            {isChild && (
              <div style={{ 
                width: "32px", 
                display: "flex", 
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0
              }}>
                <div style={{
                  width: "2px",
                  height: "100%",
                  backgroundColor: "#CBD5E1",
                  marginRight: "8px"
                }} />
              </div>
            )}

            {/* Ticket Info */}
            <div style={{ flex: 1 }}>
              <div style={{ 
                display: "flex", 
                alignItems: "center", 
                gap: "12px", 
                marginBottom: "8px" 
              }}>
                <span style={{ 
                  fontSize: "15px", 
                  fontWeight: "600", 
                  color: "#111827"
                }}>
                  {ticket.id}
                  {isChild && (
                    <span style={{
                      marginLeft: "8px",
                      padding: "2px 8px",
                      borderRadius: "4px",
                      fontSize: "11px",
                      fontWeight: "600",
                      backgroundColor: "#FEF3C7",
                      color: "#92400E"
                    }}>
                      PARENT TICKET
                    </span>
                  )}
                  </span>
                {/* Badge for reopened tickets - FIXED LINE BELOW */}
                {!isChild && ticket.isReopened && (
                  <span style={{
                    marginLeft: "8px",
                    padding: "2px 8px",
                    borderRadius: "4px",
                    fontSize: "11px",
                    fontWeight: "600",
                    backgroundColor: "#FEE2E2",
                    color: "#991B1B"
                  }}>
                    Reopen
                  </span>
                )}
                <div style={{
                  padding: "3px 10px",
                  borderRadius: "4px",
                  fontSize: "12px",
                  fontWeight: "600",
                  backgroundColor: statusStyle.bg,
                  color: statusStyle.color
                }}>
                  {statusStyle.icon} {ticket.status}
                </div>
              </div>
              
              <div style={{ 
                fontSize: "13px", 
                color: "#6B7280",
                display: "flex",
                alignItems: "center",
                gap: "12px",
                flexWrap: "wrap"
              }}>
                <span style={{ fontWeight: "500", color: "#374151" }}>
                  {ticket.bank}
                </span>
                <span style={{ color: "#D1D5DB" }}>•</span>
                <span>{ticket.branch}</span>
                <span style={{ color: "#D1D5DB" }}>•</span>
                <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  <Clock size={13} />
                  {ticket.date}
                </span>
              </div>
            </div>
          </div>

          {/* Right Section */}
          <div style={{ 
            display: "flex", 
            gap: "24px", 
            alignItems: "center",
            paddingLeft: "24px",
            borderLeft: "1px solid #E2E8F0"
          }}>
            <div style={{ textAlign: "right", minWidth: "120px" }}>
              <div style={{ 
                fontSize: "11px", 
                color: "#9CA3AF",
                marginBottom: "4px",
                textTransform: "uppercase",
                letterSpacing: "0.5px"
              }}>
                Reported By
              </div>
              <div style={{ 
                fontSize: "13px", 
                fontWeight: "500", 
                color: "#111827",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: "6px"
              }}>
                <User size={13} color="#6B7280" />
                {ticket.requesterBy}
              </div>
            </div>

            <div style={{ textAlign: "right", minWidth: "120px" }}>
              <div style={{ 
                fontSize: "11px", 
                color: "#9CA3AF",
                marginBottom: "4px",
                textTransform: "uppercase",
                letterSpacing: "0.5px"
              }}>
                Support Team
              </div>
              <div style={{ 
                fontSize: "13px", 
                fontWeight: "500", 
                color: "#111827",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: "6px"
              }}>
                <Users size={13} color="#6B7280" />
                {ticket.handler}
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{ display: "flex", gap: "8px" }}>
              {/* View Details */}
              <button
                onClick={() => handleViewDetails(ticket)}
                disabled={exporting}
                style={{ 
                  width: "36px",
                  height: "36px",
                  borderRadius: "6px",
                  backgroundColor: "#F3F4F6",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.15s ease",
                  border: "none",
                  cursor: exporting ? "not-allowed" : "pointer",
                  color: "#6B7280",
                  opacity: exporting ? 0.5 : 1
                }}
                title="View Details"
              >
                <Eye size={16} id="view-details-icon" />
              </button>
              
              {/* Export Excel */}
              <button
                onClick={() => exportIndividualToExcel(ticket.ticketIdInternal)}
                disabled={exporting}
                style={{ 
                  width: "36px",
                  height: "36px",
                  borderRadius: "6px",
                  backgroundColor: "#ECFDF5",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.15s ease",
                  border: "none",
                  cursor: exporting ? "not-allowed" : "pointer",
                  color: "#10B981",
                  opacity: exporting ? 0.5 : 1
                }}
                title="Export to Excel"
              >
                <FileSpreadsheet size={16} />
              </button>
              
              {/* Export PDF */}
              <button
                onClick={() => exportIndividualToPDF(ticket.ticketIdInternal)}
                disabled={exporting}
                style={{ 
                  width: "36px",
                  height: "36px",
                  borderRadius: "6px",
                  backgroundColor: "#FEF2F2",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.15s ease",
                  border: "none",
                  cursor: exporting ? "not-allowed" : "pointer",
                  color: "#EF4444",
                  opacity: exporting ? 0.5 : 1
                }}
                title="Export to PDF"
              >
                <FileTextIcon size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* Expanded Child Ticket */}
        {!isChild && isExpanded && ticket.oldGrievanceData && (
          <div style={{
            borderTop: "2px dashed #CBD5E1",
            backgroundColor: "#F8FAFC"
          }}>
            {ticket.oldGrievanceData
              .slice()
              .sort((a, b) => (b.seqNo || 0) - (a.seqNo || 0)) // newest first (highest seqNo)
              .map((oldTicket) => renderTicketCard({
                id: oldTicket.grievanceSeqId,
                bank: oldTicket.bankNameShort,
                branch: oldTicket.branchNameShort,
                date: formatDateTime(oldTicket.ticketDateAndTime || oldTicket.created_on),
                status: getStatusFromId(oldTicket.status),
                priority: oldTicket.priority,
                requesterBy: oldTicket.requesterBy,
                handler: oldTicket.assignedTeamName,
                assignToUserName: oldTicket.assignToUserName || "Unassigned",
                originalData: oldTicket,
                auditCount: calculateAuditCount(oldTicket),
                hasOldGrievance: false,
                oldGrievanceData: null,
                ticketIdInternal: oldTicket.id
              }, true))
            }
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    fetchUserProfile();
  }, []);

  useEffect(() => {
    if (userId) {
      getAllGrievanceData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    if (isLoaded) {
      applyFilters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, dateRange, selectedStatus, complaints]);

  // Replace the return statement with this updated one:

  return (
    <div style={{ 
      minHeight: "100vh",
      backgroundColor: "#FAFAFA",
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      padding: "10px 24px"
    }}>
      <Loader show={loader} type="body" stack="vertical" message="Loading..." />

      <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
        {/* Header */}
       

        {/* Filters */}
        <div style={{
          backgroundColor: "white",
          padding: dateError ? "10px 10px 30px 10px" : "10px",
          borderRadius: "8px",
          marginBottom: "10px",
          border: "1px solid #E5E7EB",
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.05)"
        }}>
          <div style={{ 
            display: "flex",
            // gridTemplateColumns: "2fr 1fr 2fr auto",
            gap: "16px",
            alignItems: "end"
          }}>
            {/* Search */}
            <div>
              <label style={{ 
                display: "block", 
                fontSize: "13px", 
                fontWeight: "500", 
                color: "#374151",
                marginBottom: "6px"
              }}>
                Search
              </label>
              <div style={{ position: "relative" }}>
                <Search 
                  size={16} 
                  style={{ 
                    position: "absolute", 
                    left: "12px", 
                    top: "50%", 
                    transform: "translateY(-50%)", 
                    color: "#9CA3AF"
                  }} 
                />
                <input
                  type="text"
                  placeholder="Search by ID, bank, user..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "9px 12px 9px 38px",
                    border: "1px solid #D1D5DB",
                    borderRadius: "6px",
                    fontSize: "14px",
                    outline: "none",
                    transition: "border-color 0.15s ease",
                    backgroundColor: "white"
                  }}
                />
              </div>
            </div>

            {/* Status Filter */}
            <div>
              <label style={{ 
                display: "block", 
                fontSize: "13px", 
                fontWeight: "500", 
                color: "#374151",
                marginBottom: "6px"
              }}>
                Status
              </label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                id="status-filter"
                style={{
                  width: "100%",
                  padding: "9px 12px",
                  border: "1px solid #D1D5DB",
                  borderRadius: "6px",
                  fontSize: "14px",
                  backgroundColor: "white",
                  cursor: "pointer",
                  outline: "none",
                  color: "#374151"
                }}
              >
                {statusOptions.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>

            {/* Date Range */}
            <div>
              <div style={{ display: "flex", gap: "8px", marginBottom: "6px" }}>
                <label style={{
                  flex: 1,
                  fontSize: "13px",
                  fontWeight: "500",
                  color: "#374151"
                }}>
                  From Date
                </label>
                <label style={{
                  flex: 1,
                  fontSize: "13px",
                  fontWeight: "500",
                  color: "#374151"
                }}>
                  To Date
                </label>
              </div>
              <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                <input
                  key={`from-date-${dateInputKey}`}
                  id="from-date-input"
                  type="date"
                  value={dateRange.from}
                  max={new Date().toISOString().split('T')[0]}
                  onChange={(e) => {
                    const newFromDate = e.target.value;
                    setDateError(""); // Clear error when from date changes
                    setDateRange(prev => {
                      // If new from date is later than current to date, clear to date
                      if (prev.to && newFromDate > prev.to) {
                        return { from: newFromDate, to: "" };
                      }
                      return { ...prev, from: newFromDate };
                    });
                  }}
                  style={{
    display: "flex",
    alignItems: "center",
    padding: "4px 12px",
    height: "38px",
    width:"139px",
    boxSizing: "border-box",
    border: `1px solid ${dateError ? "#EF4444" : "#D1D5DB"}`,
    borderRadius: "6px",
    fontSize: "13px",
    outline: "none",
    color: "#374151"
}}
                />
                <span style={{ color: "#9CA3AF", fontSize: "13px" }}>–</span>
                <input
                  key={`to-date-${dateInputKey}`}
                  id="to-date-input"
                  type="date"
                  value={dateRange.to}
                  max={new Date().toISOString().split('T')[0]}
                  onChange={(e) => {
                    const newToDate = e.target.value;

                    // Always update the state first
                    setDateRange(prev => ({ ...prev, to: newToDate }));

                    // Then validate and set/clear error
                    if (newToDate && dateRange.from && newToDate < dateRange.from) {
                      setDateError("To Date cannot be earlier than From Date");
                    } else {
                      setDateError("");
                    }
                  }}
                  disabled={!dateRange.from}
                  style={{
    display: "flex",
    alignItems: "center",
    padding: "4px 12px",
    height: "38px",
    width:"139px",
    boxSizing: "border-box",
    border: `1px solid ${dateError ? "#EF4444" : "#D1D5DB"}`,
    borderRadius: "6px",
    fontSize: "13px",
    outline: "none",
    color: "#374151",
    backgroundColor: !dateRange.from ? "#F9FAFB" : "white",
    cursor: !dateRange.from ? "not-allowed" : "auto",
    opacity: !dateRange.from ? 0.6 : 1
}}
                />
              </div>
              {/* Error Message */}
              {dateError && (
                <div style={{
                  marginTop: "6px",
                  fontSize: "12px",
                  color: "#EF4444",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  position: "absolute",
                }}>
                  <span>⚠</span>
                  <span>{dateError}</span>
                </div>
              )}
            </div>

            {/* Clear Button */}
            <button
              onClick={clearFilters}
              id="clear-filters-btn"
              style={{
                padding: "9px 16px",
                backgroundColor: "white",
                color: "#6B7280",
                border: "1px solid #D1D5DB",
                borderRadius: "6px",
                fontSize: "14px",
                fontWeight: "500",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                transition: "all 0.15s ease",
                whiteSpace: "nowrap"
              }}
            >
              <X size={16} id="clear-filters"/>
              Clear
            </button>
          </div>
        </div>

        {/* Results Summary */}
        {filteredComplaints.length > 0 && (
  <div style={{
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "16px"
  }}>
    {/* Results Summary */}
    <div style={{
      fontSize: "13px",
      color: "#6B7280",
      display: "flex",
      alignItems: "center",
      gap: "8px"
    }}>
      <FileText size={16} />
      <span>
        {totalItems} {totalItems === 1 ? 'Ticket' : 'Tickets'} Found
        {selectedStatus !== "All Status" && ` • Status: ${selectedStatus}`}
      </span>
    </div>

        <div style={{ position: "relative"}}>
          <button
            onClick={() => setIsExportDropdownOpen(!isExportDropdownOpen)}
            style={{
              padding: "9px 16px",
              backgroundColor: "#3B82F6",
              color: "white",
              border: "none",
              borderRadius: "6px",
              fontSize: "14px",
              fontWeight: "500",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              transition: "all 0.15s ease"
            }}
          >
            <Download size={16} />
            Export
            {isExportDropdownOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>

          {isExportDropdownOpen && (
            <div style={{
              position: "absolute",
              top: "100%",
              right: 0,
              marginTop: "8px",
              backgroundColor: "white",
              borderRadius: "8px",
              border: "1px solid #E5E7EB",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
              minWidth: "180px",
              zIndex: 100
            }}>
              <button
                onClick={() => {
                  handleExportAllToExcel();
                  setIsExportDropdownOpen(false);
                }}
                disabled={exporting || filteredComplaints.length === 0}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  backgroundColor: "white",
                  color: "#10B981",
                  border: "none",
                  borderBottom: "1px solid #F3F4F6",
                  fontSize: "14px",
                  fontWeight: "500",
                  cursor: exporting || filteredComplaints.length === 0 ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  opacity: exporting || filteredComplaints.length === 0 ? 0.5 : 1,
                  borderTopLeftRadius: "8px",
                  borderTopRightRadius: "8px",
                  transition: "all 0.15s ease"
                }}
              >
                <FileSpreadsheet size={16} />
                Export to Excel
              </button>

              <button
                onClick={() => {
                  handleExportAllToPDF();
                  setIsExportDropdownOpen(false);
                }}
                disabled={exporting || filteredComplaints.length === 0}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  backgroundColor: "white",
                  color: "#EF4444",
                  border: "none",
                  fontSize: "14px",
                  fontWeight: "500",
                  cursor: exporting || filteredComplaints.length === 0 ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  opacity: exporting || filteredComplaints.length === 0 ? 0.5 : 1,
                  borderBottomLeftRadius: "8px",
                  borderBottomRightRadius: "8px",
                  transition: "all 0.15s ease"
                }}
              >
                <FileTextIcon size={16} />
                Export to PDF
              </button>
            </div>
          )}
        </div>
</div>
)}
        {/* Tickets List */}
        <div style={{ marginBottom: "20px" }}>
          {filteredComplaints.length === 0 ? (
            <div style={{
              backgroundColor: "white",
              padding: "64px 32px",
              borderRadius: "8px",
              textAlign: "center",
              border: "1px solid #E5E7EB"
            }}>
              <div style={{
                width: "48px",
                height: "48px",
                borderRadius: "50%",
                backgroundColor: "#F3F4F6",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px"
              }}>
                <FileText size={24} color="#9CA3AF" />
              </div>
              <h3 style={{ 
                fontSize: "16px", 
                fontWeight: "600", 
                color: "#111827",
                margin: "0 0 6px 0"
              }}>
                No Records Found
              </h3>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {currentItems.map((ticket) => renderTicketCard(ticket))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {filteredComplaints.length > 0 && (
          <div style={{
            backgroundColor: "white",
            padding: "6px 10px",
            borderRadius: "8px",
            border: "1px solid #E5E7EB",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}>
            <div style={{ fontSize: "13px", color: "#6B7280" }}>
              Showing {startIndex + 1}–{endIndex} of {totalItems}
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                style={{
                  padding: "6px 12px",
                  backgroundColor: "white",
                  color: currentPage === 1 ? "#D1D5DB" : "#374151",
                  border: "1px solid #E5E7EB",
                  borderRadius: "6px",
                  fontSize: "13px",
                  fontWeight: "500",
                  cursor: currentPage === 1 ? "not-allowed" : "pointer",
                  transition: "all 0.15s ease"
                }}
              >
                Previous
              </button>

              <div style={{ display: "flex", gap: "4px" }}>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      style={{
                        minWidth: "36px",
                        height: "36px",
                        padding: "0",
                        backgroundColor: currentPage === pageNum ? "#3B82F6" : "white",
                        color: currentPage === pageNum ? "white" : "#374151",
                        border: `1px solid ${currentPage === pageNum ? "#3B82F6" : "#E5E7EB"}`,
                        borderRadius: "6px",
                        fontSize: "13px",
                        fontWeight: "500",
                        cursor: "pointer",
                        transition: "all 0.15s ease"
                      }}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                {totalPages > 5 && currentPage < totalPages - 2 && (
                  <>
                    <span style={{ 
                      display: "flex", 
                      alignItems: "center", 
                      color: "#D1D5DB", 
                      padding: "0 4px" 
                    }}>
                      ...
                    </span>
                    <button
                      onClick={() => handlePageChange(totalPages)}
                      style={{
                        minWidth: "36px",
                        height: "36px",
                        padding: "0",
                        backgroundColor: "white",
                        color: "#374151",
                        border: "1px solid #E5E7EB",
                        borderRadius: "6px",
                        fontSize: "13px",
                        fontWeight: "500",
                        cursor: "pointer",
                        transition: "all 0.15s ease"
                      }}
                    >
                      {totalPages}
                    </button>
                  </>
                )}
              </div>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                style={{
                  padding: "6px 12px",
                  backgroundColor: "white",
                  color: currentPage === totalPages ? "#D1D5DB" : "#374151",
                  border: "1px solid #E5E7EB",
                  borderRadius: "6px",
                  fontSize: "13px",
                  fontWeight: "500",
                  cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                  transition: "all 0.15s ease"
                }}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditHistory;